import { useEffect, useMemo, useRef, useState, type ComponentType, type CSSProperties } from 'react';
import {
  BuildingOfficeIcon,
  CameraIcon,
  RocketLaunchIcon,
  ArrowDownTrayIcon,
  BeakerIcon,
  ChartBarIcon,
  CpuChipIcon,
  Bars3BottomLeftIcon,
  Bars3Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PaintBrushIcon,
  RectangleStackIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  MapPinIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Droplets, FlaskConical, Thermometer, Waves, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { CONFIGURATION_SECTIONS } from '@/app/utils/aql-configuration-seed';
import type {
  AqlCamera,
  AqlClient,
  AqlConfigurationDataset,
  AqlKit,
  AqlLocation,
  AqlPhase,
  AqlProject,
  AqlSensor,
  AqlUltrasoundDevice,
  AqlUserAccess,
  AqlZone,
  ConfigurationSectionDefinition
} from '@/app/types/aql-configuration';
import { ConfigurationSectionPage } from './configuration-section-page';
import { KitVisionRuntimeControls } from './kit-vision-runtime-controls';
import { AlertRuleManager } from '../AlertRuleManager';
import {
  deleteClientCascade,
  deleteCameraCascade,
  deleteConfigurationRecord,
  deleteKitCascade,
  deleteLocationCascade,
  deleteProjectCascade,
  deleteSensorCascade,
  deleteUltrasoundDeviceCascade,
  deleteZoneCascade,
  EMPTY_CONFIGURATION_DATASET,
  loadConfigurationDataset,
  saveConfigurationRecord,
  saveConfigurationUserAccess,
  type ConfigurationPortalAccess
} from '@/app/utils/aql-configuration-supabase';
import { useGlobal, type ClientScope } from '@/app/providers/global-provider';
import { ConfigurationDetailSheet, type ConfigurationFieldDefinition } from './configuration-detail-sheet';
import { AqlVisionLoader } from '../branding/aql-vision-loader';
import { useTranslation } from '@/app/hooks/useTranslation';
import { PersonalizationSettings } from '../personalization-settings';
import { supabase } from '/utils/supabase/client';
import { functionsBaseUrl, publicAnonKey, supabaseUrl } from '/utils/supabase/info';
import { OperationalArchitectureFlow } from './operational-architecture-flow';
import { getDefaultOperationalLocationType, getOperationalContextProfile, OPERATIONAL_CONTEXT_OPTIONS } from '@/app/utils/operational-context';

type SectionId = ConfigurationSectionDefinition['id'];
type AccessMode = 'admin' | 'client';
type ConfigurationViewId = SectionId | 'operational-architecture';
const OPERATIONAL_ARCHITECTURE_SECTION_IDS: SectionId[] = ['client', 'projects', 'locations', 'zones', 'kits', 'cameras', 'sensors', 'ultrasound', 'thresholds'];
const STANDALONE_SECTION_IDS: SectionId[] = ['vision-copilot', 'users', 'personalization'];
const ARCHITECTURE_EDITABLE_SECTION_IDS: SectionId[] = ['client', 'projects', 'locations', 'zones', 'kits', 'cameras', 'sensors', 'ultrasound', 'thresholds'];

interface ConfigurationModuleProps {
  accessMode?: AccessMode;
  clientSlug?: string | null;
  projectSlug?: string | null;
  portalAccess?: ConfigurationPortalAccess | null;
}

const iconMap: Record<SectionId, ComponentType<{ className?: string }>> = {
  client: BuildingOfficeIcon,
  projects: Bars3BottomLeftIcon,
  locations: MapPinIcon,
  zones: ArrowTrendingUpIcon,
  kits: CpuChipIcon,
  cameras: CameraIcon,
  ultrasound: RectangleStackIcon,
    sensors: Cog6ToothIcon,
    'vision-copilot': RocketLaunchIcon,
    'species-phases': BeakerIcon,
    thresholds: ShieldCheckIcon,
  users: UserGroupIcon,
  personalization: PaintBrushIcon
};

const sensorIconMap: Record<string, ComponentType<{ className?: string }>> = {
  temperature: Thermometer,
  sensor_temperature: Thermometer,
  ph: FlaskConical,
  sensor_ph: FlaskConical,
  dissolved_oxygen: Droplets,
  oxygen: Droplets,
  sensor_dissolved_oxygen: Droplets,
  redox: Zap,
  orp: Zap,
  sensor_redox: Zap,
  salinity: Waves,
  sensor_salinity: Waves
};

const sensorLabelMap: Record<string, string> = {
  temperature: 'Temperatura',
  sensor_temperature: 'Temperatura',
  ph: 'pH',
  sensor_ph: 'pH',
  dissolved_oxygen: 'O2 dissolvido',
  oxygen: 'O2 dissolvido',
  sensor_dissolved_oxygen: 'O2 dissolvido',
  redox: 'Redox / ORP',
  orp: 'Redox / ORP',
  sensor_redox: 'Redox / ORP',
  salinity: 'Salinidade',
  sensor_salinity: 'Salinidade'
};

const sensorToneClass = (sensorType: string) => {
  const normalized = sensorType.toLowerCase();
  if (normalized.includes('temperature')) return 'border-orange-500/25 bg-orange-500/10 text-orange-700 dark:text-orange-300';
  if (normalized.includes('ph')) return 'border-sky-500/25 bg-sky-500/10 text-sky-700 dark:text-sky-300';
  if (normalized.includes('oxygen')) return 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
  if (normalized.includes('redox') || normalized.includes('orp')) return 'border-purple-500/25 bg-purple-500/10 text-purple-700 dark:text-purple-300';
  return 'border-cyan-500/25 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300';
};

const architectureNodeToneClass = (tone: 'client' | 'project' | 'location' | 'zone' | 'kit' | 'camera' | 'sensor' | 'ultrasound' | 'threshold') => {
  const tones = {
    client: 'border-slate-300 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-50',
    project: 'border-sky-500/25 bg-sky-500/10 text-sky-900 dark:text-sky-100',
    location: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100',
    zone: 'border-indigo-500/25 bg-indigo-500/10 text-indigo-900 dark:text-indigo-100',
    kit: 'border-orange-500/25 bg-orange-500/10 text-orange-900 dark:text-orange-100',
    camera: 'border-violet-500/25 bg-violet-500/10 text-violet-900 dark:text-violet-100',
    sensor: 'border-blue-500/25 bg-blue-500/10 text-blue-900 dark:text-blue-100',
    ultrasound: 'border-cyan-500/25 bg-cyan-500/10 text-cyan-900 dark:text-cyan-100',
    threshold: 'border-rose-500/25 bg-rose-500/10 text-rose-900 dark:text-rose-100'
  };
  return tones[tone];
};

const formatCount = (count: number, singular: string, plural: string) => `${count} ${count === 1 ? singular : plural}`;

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
const normalizeCameraCode = (value: unknown) => slugify(String(value ?? '')).slice(0, 80);

const projectCoverRequestHeaders = async () => {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;
  if (!accessToken) throw new Error('A sessão expirou. Inicia sessão novamente.');
  return { Authorization: `Bearer ${accessToken}`, apikey: publicAnonKey };
};

const uploadProjectCover = async (file: File, draft: Record<string, any>) => {
  const projectId = toId(draft.project_id);
  const clientId = toId(draft.client_id);
  if (!projectId || !clientId) throw new Error('Define primeiro o cliente e o projeto.');
  const formData = new FormData();
  formData.set('image', file);
  formData.set('project_id', projectId);
  formData.set('client_id', clientId);
  let response: Response;
  try {
    response = await fetch(`${functionsBaseUrl}/vision-project-cover-upload`, {
      method: 'POST',
      headers: await projectCoverRequestHeaders(),
      body: formData
    });
  } catch {
    throw new Error('Não foi possível contactar o serviço de imagens. Tenta novamente dentro de instantes.');
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.url) throw new Error(payload?.error ?? 'Não foi possível carregar a imagem.');
  return String(payload.url);
};

const removeProjectCover = async (draft: Record<string, any>) => {
  let response: Response;
  try {
    response = await fetch(`${functionsBaseUrl}/vision-project-cover-upload`, {
      method: 'DELETE',
      headers: { ...(await projectCoverRequestHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_id: draft.project_id, client_id: draft.client_id })
    });
  } catch {
    throw new Error('Não foi possível contactar o serviço de imagens. Tenta novamente dentro de instantes.');
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error ?? 'Não foi possível remover a imagem.');
};
const liveFeedPathForCameraCode = (code: unknown) => {
  const normalizedCode = normalizeCameraCode(code);
  return normalizedCode ? `live/${normalizedCode}/feed.jpg` : '';
};
const trainingBurstPathForCameraCode = (code: unknown) => {
  const normalizedCode = normalizeCameraCode(code);
  return normalizedCode ? `live/${normalizedCode}/training` : '';
};
const clampTrainingTarget = (value: unknown) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.max(1, Math.min(500, Math.floor(parsed)));
};
const slugSegments = (value: unknown) =>
  String(value ?? '')
    .split(/[/?#]+/)
    .map((segment) => slugify(segment))
    .filter(Boolean);
const toId = (value: unknown) => String(value ?? '').trim();
const toQueryPart = (value: unknown) =>
  String(value ?? '')
    .replace(/\s*\/\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .trim();
const CLIENT_ADMIN_ROLE = 'client_admin';
const VISION_COPILOT_SETTING_KEY = 'vision_copilot';
const DEFAULT_COPILOT_PROMPT = `Tu és um biólogo especialista em aquacultura e o cérebro do AQL Vision Copilot.
Recebeste um alerta visual com {{suspect_percent}}% de suspeita.
Analisa a imagem em anexo e cruza-a estritamente com os dados em tempo real fornecidos: pH, O2, salinidade, espécie, fase e tanque.
Dá um diagnóstico operacional claro, conciso e focado em ações preventivas.
Não inventes métricas ausentes. Se faltar contexto, diz que o dado não foi fornecido.`;
const projectMatchesPath = (project: AqlProject, pathSlug: string) => {
  const normalizedPath = slugify(pathSlug);
  const slugParts = slugSegments(project.slug);
  return (
    slugify(project.slug) === normalizedPath ||
    slugParts[slugParts.length - 1] === normalizedPath ||
    slugify(project.name) === normalizedPath
  );
};

type EdgeExportEntityType = 'kit' | 'camera' | 'sensor';

interface EdgeExportRequest {
  type: EdgeExportEntityType;
  label: string;
  client: AqlClient | null;
  project: AqlProject | null;
  dataset: AqlConfigurationDataset;
  kit?: AqlKit | null;
  camera?: AqlCamera | null;
  sensor?: AqlSensor | null;
  all?: boolean;
}

const cleanPayload = <T,>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map((item) => cleanPayload(item)).filter((item) => item !== undefined) as T;
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => [key, cleanPayload(entry)])
    ) as T;
  }

  return value;
};

const downloadJsonFile = (payload: Record<string, unknown>, fileName: string) => {
  if (typeof window === 'undefined') return;
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const copyJsonToClipboard = async (payload: Record<string, unknown>) => {
  if (typeof navigator === 'undefined' || !navigator.clipboard) return false;
  await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
  return true;
};

const buildEdgeExportPayload = ({
  type,
  label,
  client,
  project,
  dataset,
  kit,
  camera,
  sensor,
  all = false
}: EdgeExportRequest) => {
  const selectedKit = kit
    ?? (camera?.kit_id ? dataset.kits.find((item) => item.kit_id === camera.kit_id) : null)
    ?? (sensor?.kit_id ? dataset.kits.find((item) => item.kit_id === sensor.kit_id) : null)
    ?? null;
  const selectedZone = selectedKit?.zone_id
    ? dataset.zones.find((item) => item.zone_id === selectedKit.zone_id) ?? null
    : (camera?.zone_id || sensor?.zone_id)
      ? dataset.zones.find((item) => item.zone_id === (camera?.zone_id ?? sensor?.zone_id)) ?? null
      : null;
  const selectedLocation = selectedZone?.location_id
    ? dataset.locations.find((item) => item.location_id === selectedZone.location_id) ?? null
    : selectedKit?.location_id
      ? dataset.locations.find((item) => item.location_id === selectedKit.location_id) ?? null
      : null;
  const resolvedClient = client
    ?? (selectedKit?.client_id ? dataset.clients.find((item) => item.client_id === selectedKit.client_id) : null)
    ?? null;
  const resolvedProject = project
    ?? (selectedKit?.project_id ? dataset.projects.find((item) => item.project_id === selectedKit.project_id) : null)
    ?? null;

  const kitIds = new Set(
    all
      ? dataset.kits.map((item) => item.kit_id)
      : [selectedKit?.kit_id, camera?.kit_id, sensor?.kit_id].filter(Boolean) as string[]
  );
  const zoneIds = new Set(
    all
      ? dataset.zones.map((item) => item.zone_id)
      : [selectedZone?.zone_id, camera?.zone_id, sensor?.zone_id].filter(Boolean) as string[]
  );
  const cameras = all
    ? dataset.cameras.filter((item) => kitIds.has(String(item.kit_id ?? '')) || zoneIds.has(String(item.zone_id ?? '')))
    : camera
      ? [camera]
      : dataset.cameras.filter((item) => selectedKit?.kit_id && item.kit_id === selectedKit.kit_id);
  const sensors = all
    ? dataset.sensors.filter((item) => kitIds.has(String(item.kit_id ?? '')) || zoneIds.has(String(item.zone_id ?? '')))
    : sensor
      ? [sensor]
      : dataset.sensors.filter((item) => selectedKit?.kit_id && item.kit_id === selectedKit.kit_id);
  const kits = all
    ? dataset.kits
    : selectedKit
      ? [selectedKit]
      : [];
  const primaryKit = kits[0] ?? selectedKit ?? null;
  const primaryCamera = cameras[0] ?? camera ?? null;
  const primarySensor = sensors[0] ?? sensor ?? null;

  return cleanPayload({
    schema: 'aql-kit.v2',
    generated_at: new Date().toISOString(),
    configuration_revision: primaryKit?.configuration_revision ?? 1,
    target: {
      export_type: all ? `${type}_collection` : type,
      label
    },
    environment: {
      supabase_url: supabaseUrl,
      functions_base_url: functionsBaseUrl
    },
    authentication: {
      header: 'X-AQL-Device-Token',
      value: '<AQL_DEVICE_TOKEN>',
      note: 'Nunca guardar tokens reais neste ficheiro se for partilhado fora da equipa técnica.'
    },
    edge_device: {
      device_id: '<optional_device_id>',
      note: 'Opcional. Usar apenas quando existir um registo em aql_devices. Para sensores associados a kit/gateway, kit_id + sensor_id são suficientes.'
    },
    endpoints: {
      device_register: `${functionsBaseUrl}/vision-device-register`,
      device_heartbeat: `${functionsBaseUrl}/vision-device-heartbeat`,
      sensor_ingest: `${functionsBaseUrl}/vision-sensor-ingest`,
      capture_ingest: `${functionsBaseUrl}/vision-capture-ingest`,
      live_capture_upload: `${functionsBaseUrl}/vision-captures`
    },
    context: {
      client_id: resolvedClient?.client_id ?? primaryKit?.client_id ?? null,
      client_name: resolvedClient?.name ?? null,
      project_id: resolvedProject?.project_id ?? primaryKit?.project_id ?? null,
      project_name: resolvedProject?.name ?? null,
      location_id: selectedLocation?.location_id ?? primaryKit?.location_id ?? null,
      location_name: selectedLocation?.name ?? null,
      zone_id: selectedZone?.zone_id ?? primaryKit?.zone_id ?? primaryCamera?.zone_id ?? primarySensor?.zone_id ?? null,
      zone_name: selectedZone?.name ?? null
    },
    runtime_defaults: {
      heartbeat_interval_seconds: 120,
      capture_interval_seconds: primaryKit?.capture_interval_seconds ?? primaryCamera?.capture_interval_seconds ?? 60,
      frame_interval_ms: primaryKit?.frame_interval_ms ?? primaryCamera?.frame_interval_ms ?? 1000,
      storage_bucket: 'captures',
      jpg_quality: 70,
      live_frame_path_template: 'live/{camera_code}/feed.jpg'
    },
    continuity: {
      offline_first: true,
      offline_buffer_hours: primaryKit?.offline_buffer_hours ?? 72,
      bulk_batch_size: primaryKit?.bulk_batch_size ?? 500,
      priority_order: ['alerts', 'heartbeat', 'sensor_readings', 'captures'],
      acknowledgement_required: true,
      idempotency_field: 'source_reading_id',
      preserve_observed_at: true
    },
    local_operation: {
      enabled: primaryKit?.local_alerting_enabled !== false,
      cloud_independent: true,
      note: 'As regras críticas devem continuar ativas no controlador durante falhas de Internet.',
      sensor_limits: sensors.map((item) => ({
        sensor_id: item.sensor_id,
        sensor_type: item.sensor_type,
        unit: item.unit ?? null,
        min_threshold: item.min_threshold ?? null,
        max_threshold: item.max_threshold ?? null,
        recommended_min: item.recommended_min ?? null,
        recommended_max: item.recommended_max ?? null
      }))
    },
    kit: primaryKit ? {
      kit_id: primaryKit.kit_id,
      name: primaryKit.name,
      kit_type: primaryKit.kit_type,
      role: 'acquisition_automation_gateway',
      controller_profile: primaryKit.controller_profile ?? 'atronia-atlas',
      connectivity_mode: primaryKit.connectivity_mode ?? 'ethernet',
      local_alerting_enabled: primaryKit.local_alerting_enabled !== false,
      configuration_revision: primaryKit.configuration_revision ?? 1,
      status: primaryKit.status,
      heartbeat: primaryKit.heartbeat ?? null,
      capture_interval_seconds: primaryKit.capture_interval_seconds ?? null,
      frame_interval_ms: primaryKit.frame_interval_ms ?? null,
      is_active: primaryKit.is_active
    } : null,
    kits: all ? kits.map((item) => ({
      kit_id: item.kit_id,
      client_id: item.client_id,
      project_id: item.project_id,
      location_id: item.location_id,
      zone_id: item.zone_id ?? null,
      name: item.name,
      kit_type: item.kit_type,
      role: 'acquisition_automation_gateway',
      controller_profile: item.controller_profile ?? 'atronia-atlas',
      connectivity_mode: item.connectivity_mode ?? 'ethernet',
      offline_buffer_hours: item.offline_buffer_hours ?? 72,
      bulk_batch_size: item.bulk_batch_size ?? 500,
      local_alerting_enabled: item.local_alerting_enabled !== false,
      configuration_revision: item.configuration_revision ?? 1,
      status: item.status,
      capture_interval_seconds: item.capture_interval_seconds ?? null,
      frame_interval_ms: item.frame_interval_ms ?? null,
      is_active: item.is_active
    })) : undefined,
    cameras: cameras.map((item) => ({
      camera_id: item.camera_id,
      kit_id: item.kit_id ?? primaryKit?.kit_id ?? null,
      zone_id: item.zone_id ?? primaryKit?.zone_id ?? null,
      name: item.name,
      camera_code: item.camera_code ?? null,
      source_type: item.source_type ?? null,
      source_uri: item.source_uri ?? null,
      live_feed_path: item.live_feed_path ?? (item.camera_code ? `live/${item.camera_code}/feed.jpg` : null),
      training_burst_available: Boolean(item.training_burst_available),
      training_burst_target: item.training_burst_target ?? 50,
      training_burst_path: item.training_burst_path ?? (item.camera_code ? `live/${item.camera_code}/training` : null),
      resolution: item.resolution ?? null,
      position_description: item.position_description ?? null,
      capture_interval_seconds: item.capture_interval_seconds ?? primaryKit?.capture_interval_seconds ?? null,
      frame_interval_ms: item.frame_interval_ms ?? primaryKit?.frame_interval_ms ?? null,
      is_primary: Boolean(item.is_primary),
      is_active: item.is_active
    })),
    sensors: sensors.map((item) => ({
      sensor_id: item.sensor_id,
      device_id: (item as Record<string, unknown>).device_id ?? null,
      kit_id: item.kit_id ?? primaryKit?.kit_id ?? null,
      zone_id: item.zone_id ?? primaryKit?.zone_id ?? null,
      sensor_type: item.sensor_type,
      name: item.name,
      unit: item.unit ?? null,
      min_threshold: item.min_threshold ?? null,
      max_threshold: item.max_threshold ?? null,
      recommended_min: item.recommended_min ?? null,
      recommended_max: item.recommended_max ?? null,
      is_active: item.is_active
    })),
    examples: {
      device_register_body: {
        client_id: resolvedClient?.client_id ?? primaryKit?.client_id ?? '<client_id>',
        project_id: resolvedProject?.project_id ?? primaryKit?.project_id ?? '<project_id>',
        location_id: selectedLocation?.location_id ?? primaryKit?.location_id ?? '<location_id>',
        zone_id: selectedZone?.zone_id ?? primaryKit?.zone_id ?? '<zone_id>',
        kit: primaryKit ? {
          kit_id: primaryKit.kit_id,
          name: primaryKit.name,
          kit_type: primaryKit.kit_type,
          capture_interval_seconds: primaryKit.capture_interval_seconds ?? 60,
          frame_interval_ms: primaryKit.frame_interval_ms ?? 1000
        } : {},
        cameras: cameras.map((item) => ({
          camera_id: item.camera_id,
          name: item.name,
          camera_code: item.camera_code,
          source_type: item.source_type,
          source_uri: item.source_uri,
          capture_interval_seconds: item.capture_interval_seconds ?? primaryKit?.capture_interval_seconds ?? 60,
          frame_interval_ms: item.frame_interval_ms ?? primaryKit?.frame_interval_ms ?? 1000
        })),
        sensors: sensors.map((item) => ({
          sensor_id: item.sensor_id,
          device_id: (item as Record<string, unknown>).device_id ?? null,
          sensor_type: item.sensor_type,
          name: item.name,
          unit: item.unit,
          min_threshold: item.min_threshold,
          max_threshold: item.max_threshold,
          recommended_min: item.recommended_min,
          recommended_max: item.recommended_max
        }))
      },
      sensor_ingest_body: {
        client_id: resolvedClient?.client_id ?? primaryKit?.client_id ?? '<client_id>',
        project_id: resolvedProject?.project_id ?? primaryKit?.project_id ?? '<project_id>',
        location_id: selectedLocation?.location_id ?? primaryKit?.location_id ?? '<location_id>',
        zone_id: selectedZone?.zone_id ?? primaryKit?.zone_id ?? '<zone_id>',
        kit_id: primaryKit?.kit_id ?? '<kit_id>',
        device_id: '<optional_device_id>',
        batch_id: '<unique_batch_id>',
        sequence_from: 1,
        sequence_to: Math.max(1, sensors.length),
        recovered_after_offline: false,
        readings: sensors.map((item) => ({
          source_reading_id: '<unique_device_reading_id>',
          observed_at: new Date().toISOString(),
          sequence: 1,
          sensor_id: item.sensor_id,
          device_id: (item as Record<string, unknown>).device_id ?? undefined,
          sensor_type: item.sensor_type,
          value: item.sensor_type === 'temperature'
            ? 30
            : item.sensor_type === 'ph'
              ? 8.5
              : item.sensor_type === 'dissolved_oxygen'
                ? 5.2
                : item.sensor_type === 'redox'
                  ? 240
                  : Number(item.last_value ?? item.max_threshold ?? item.min_threshold ?? 0),
          unit: item.unit ?? null,
          metadata: {
            raw_value: null,
            calibrated_value: null,
            signal_quality: 100
          }
        }))
      },
      device_heartbeat_body: {
        client_id: resolvedClient?.client_id ?? primaryKit?.client_id ?? '<client_id>',
        project_id: resolvedProject?.project_id ?? primaryKit?.project_id ?? '<project_id>',
        zone_id: selectedZone?.zone_id ?? primaryKit?.zone_id ?? '<zone_id>',
        kit_id: primaryKit?.kit_id ?? '<kit_id>',
        device_id: '<optional_device_id>',
        camera_ids: cameras.map((item) => item.camera_id),
        camera_codes: cameras.map((item) => item.camera_code).filter(Boolean),
        sensor_ids: sensors.map((item) => item.sensor_id),
        status: 'online',
        timestamp: '<ISO-8601 timestamp>'
      },
      capture_ingest_body: {
        client_id: resolvedClient?.client_id ?? primaryKit?.client_id ?? '<client_id>',
        project_id: resolvedProject?.project_id ?? primaryKit?.project_id ?? '<project_id>',
        location_id: selectedLocation?.location_id ?? primaryKit?.location_id ?? '<location_id>',
        zone_id: selectedZone?.zone_id ?? primaryKit?.zone_id ?? '<zone_id>',
        kit_id: primaryKit?.kit_id ?? '<kit_id>',
        camera_id: primaryCamera?.camera_id ?? '<camera_id>',
        camera_code: primaryCamera?.camera_code ?? '<camera_code>',
        image_url: '<public_or_signed_image_url>',
        captured_at: '<ISO-8601 timestamp>'
      }
    }
  });
};

const exportEdgePayload = async (request: EdgeExportRequest) => {
  const payload = buildEdgeExportPayload(request);
  const fileName = `aql-edge-${slugify(request.label || request.type)}-${new Date().toISOString().slice(0, 10)}.json`;
  downloadJsonFile(payload, fileName);
  try {
    const copied = await copyJsonToClipboard(payload);
    toast.success(copied ? 'JSON copiado e descarregado' : 'JSON descarregado');
  } catch {
    toast.success('JSON descarregado');
  }
};
const inferRouteScope = () => {
  if (typeof window === 'undefined') {
    return { accessMode: 'admin' as AccessMode, clientSlug: null, projectSlug: null };
  }

  const hashPath = window.location.hash.startsWith('#/') ? window.location.hash.slice(1) : '';
  const path = hashPath || window.location.pathname;
  const segments = path.split('/').filter(Boolean);
  const first = segments[0] ?? null;

  if (!first || first === 'admin') {
    return { accessMode: 'admin' as AccessMode, clientSlug: null, projectSlug: null };
  }

  return {
    accessMode: 'client' as AccessMode,
    clientSlug: first,
    projectSlug: segments[1] ?? null
  };
};

const makeFieldsReadOnly = (
  fields: ConfigurationFieldDefinition[],
  keys: string[]
) => fields.map((field) => (keys.includes(field.key) ? { ...field, readOnly: true } : field));

const hideFields = (
  fields: ConfigurationFieldDefinition[],
  keys: string[]
) => fields.map((field) => (keys.includes(field.key) ? { ...field, hidden: true } : field));

const buildClientCatalog = (clients: AqlClient[], projects: AqlProject[]): ClientScope[] =>
  clients.map((item) => {
    const defaultProject = projects.find((project) => project.client_id === item.client_id);
    return {
      id: item.client_id,
      name: item.name,
      slug: item.slug,
      logo_url: item.logo_url ?? undefined,
      primary_color: item.primary_color ?? undefined,
      secondary_color: item.secondary_color ?? undefined,
      species_focus: defaultProject?.species_focus ?? undefined,
      default_phase: (defaultProject?.default_phase as ClientScope['default_phase']) ?? undefined
    };
  });

const matchesClientUser = (user: AqlUserAccess, client: AqlClient) => {
  if ((user.client_ids ?? []).includes(client.client_id)) return true;
  if (user.client_id) return user.client_id === client.client_id;
  const userEmail = user.email.toLowerCase();
  const clientSlug = slugify(client.slug);
  const clientName = slugify(client.name);
  return Boolean(
    (clientSlug && userEmail.includes(clientSlug)) ||
    (clientName && userEmail.includes(clientName))
  );
};

const matchesPortalUser = (user: AqlUserAccess, access: ConfigurationPortalAccess | null | undefined) => {
  if (!access) return false;
  const accessEmail = String(access.email ?? '').trim().toLowerCase();
  const userEmail = String(user.email ?? '').trim().toLowerCase();
  return Boolean(
    (access.authUserId && user.auth_user_id && access.authUserId === user.auth_user_id) ||
    (accessEmail && userEmail && accessEmail === userEmail)
  );
};

const clientMatchesPath = (client: AqlClient, pathSlug: string) =>
  slugify(client.slug) === slugify(pathSlug) || slugify(client.name) === slugify(pathSlug);

const normalizeRecordForSave = <K extends keyof AqlConfigurationDataset>(
  key: K,
  item: AqlConfigurationDataset[K][number]
): AqlConfigurationDataset[K][number] => {
  if (key !== 'clients' && key !== 'projects') return item;

  const payload = { ...(item as Record<string, any>) };
  delete payload.type;
  payload.slug = slugify(String(payload.slug || payload.name || ''));
  return payload as AqlConfigurationDataset[K][number];
};

const isGlobalPhase = (phase: AqlPhase) => !phase.client_id && !phase.project_id;

const buildPhaseSelectOptions = (phases: AqlPhase[], fallbackNames: Array<string | null | undefined> = []) => {
  const optionMap = new Map<string, { value: string; label: string; sortOrder: number | null }>();

  phases.forEach((phase) => {
    const name = phase.name.trim();
    if (!name || !phase.is_active) return;

    const key = slugify(name) || name.toLowerCase();
    const nextOption = {
      value: name,
      label: name,
      sortOrder: phase.sort_order ?? null
    };
    const currentOption = optionMap.get(key);

    if (!currentOption) {
      optionMap.set(key, nextOption);
      return;
    }

    const currentSort = currentOption.sortOrder ?? Number.POSITIVE_INFINITY;
    const nextSort = nextOption.sortOrder ?? Number.POSITIVE_INFINITY;
    if (nextSort < currentSort) optionMap.set(key, nextOption);
  });

  fallbackNames.forEach((value) => {
    const name = String(value ?? '').trim();
    if (!name) return;

    const key = slugify(name) || name.toLowerCase();
    if (!optionMap.has(key)) {
      optionMap.set(key, { value: name, label: name, sortOrder: null });
    }
  });

  return Array.from(optionMap.values())
    .sort((a, b) => {
      const sortA = a.sortOrder ?? Number.POSITIVE_INFINITY;
      const sortB = b.sortOrder ?? Number.POSITIVE_INFINITY;
      if (sortA !== sortB) return sortA - sortB;
      return a.label.localeCompare(b.label);
    })
    .map(({ value, label }) => ({ value, label }));
};

export function ConfigurationModule({
  accessMode,
  clientSlug = null,
  projectSlug = null,
  portalAccess = null
}: ConfigurationModuleProps) {
  const { t: translate, lang } = useTranslation();
  const tr = (key: string, params?: Record<string, unknown>) => translate('configuration', key, params);
  const routeScope = useMemo(() => inferRouteScope(), []);
  const resolvedAccessMode = accessMode ?? routeScope.accessMode;
  const resolvedClientSlug = clientSlug ?? routeScope.clientSlug;
  const resolvedProjectSlug = projectSlug ?? routeScope.projectSlug;
  const isClientMode = resolvedAccessMode === 'client';
  const { client: selectedClient, setClient, setAvailableClients } = useGlobal();
  const [dataset, setDataset] = useState<AqlConfigurationDataset>(EMPTY_CONFIGURATION_DATASET);
  const [activeSection, setActiveSection] = useState<SectionId>('client');
  const [activeView, setActiveView] = useState<ConfigurationViewId>('operational-architecture');
  const [architectureSelection, setArchitectureSelection] = useState<{
    sectionId: SectionId;
    value: Record<string, any>;
    mode: 'create' | 'edit';
    draftNodeId?: string;
  } | null>(null);
  const savedDraftNodeIdsRef = useRef<Set<string>>(new Set());
  const [discardDraftNodeId, setDiscardDraftNodeId] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('aql_config_sidebar_collapsed') === 'true';
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadRevision, setLoadRevision] = useState(0);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token ?? portalAccess?.accessToken;
        const result = await loadConfigurationDataset(accessToken);
        if (!active) return;

        if (result.warning && result.data.clients.length === 0) {
          throw new Error(result.warning);
        }

        if (result.warning) {
          console.warn('AQL Vision: configuration loaded with partial errors', result.warning);
        }
        setDataset(result.data);
      } catch (error: any) {
        if (!active) return;
        setDataset(EMPTY_CONFIGURATION_DATASET);
        setLoadError(error?.message ?? 'Não foi possível carregar os dados de configuração.');
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [loadRevision, portalAccess?.accessToken]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('aql_config_sidebar_collapsed', String(navCollapsed));
  }, [navCollapsed]);

  const scopedDataset = useMemo(() => {
    if (!isClientMode || !resolvedClientSlug) return dataset;

    const scopedClient = dataset.clients.find((item) => clientMatchesPath(item, resolvedClientSlug)) ?? null;
    if (!scopedClient) return { ...EMPTY_CONFIGURATION_DATASET, clients: [] };

    const clientProjects = dataset.projects.filter((item) => item.client_id === scopedClient.client_id);
    const scopedProject = resolvedProjectSlug
      ? clientProjects.find((item) => projectMatchesPath(item, resolvedProjectSlug)) ?? null
      : null;
    const projects = clientProjects;
    const scopedProjects = scopedProject ? [scopedProject] : clientProjects;
    const projectIds = new Set(scopedProjects.map((item) => item.project_id));
    const locations = dataset.locations.filter((item) => item.client_id === scopedClient.client_id || projectIds.has(item.project_id));
    const locationIds = new Set(locations.map((item) => item.location_id));
    const zones = dataset.zones.filter((item) => item.client_id === scopedClient.client_id || projectIds.has(item.project_id) || locationIds.has(item.location_id));
    const zoneIds = new Set(zones.map((item) => item.zone_id));
    const kits = dataset.kits.filter((item) => projectIds.has(item.project_id) || (item.zone_id ? zoneIds.has(item.zone_id) : false));
    const kitIds = new Set(kits.map((item) => item.kit_id));
    const cameras = dataset.cameras.filter((item) => (item.kit_id ? kitIds.has(item.kit_id) : false) || (item.zone_id ? zoneIds.has(item.zone_id) : false));
    const ultrasoundDevices = dataset.ultrasoundDevices.filter((item) => kitIds.has(item.kit_id));
    const sensors = dataset.sensors.filter((item) => kitIds.has(item.kit_id) || (item.zone_id ? zoneIds.has(item.zone_id) : false));
    const species = dataset.species.filter((item) => item.client_id === scopedClient.client_id || projectIds.has(item.project_id));
    const phases = dataset.phases.filter((item) => item.client_id === scopedClient.client_id || projectIds.has(item.project_id));
    const projectSettings = dataset.projectSettings.filter((item) => item.client_id === scopedClient.client_id || projectIds.has(item.project_id));
    const users = dataset.users.filter((item) => {
      if (!matchesClientUser(item, scopedClient)) return false;
      if (isClientMode && !matchesPortalUser(item, portalAccess)) return false;
      if (!scopedProject?.project_id) return true;
      const userProjectIds = new Set([
        ...((item.project_ids ?? []).map(String).filter(Boolean)),
        ...(item.project_id ? [item.project_id] : [])
      ]);
      return userProjectIds.size === 0 || userProjectIds.has(scopedProject.project_id);
    });

    return {
      clients: [scopedClient],
      projects,
      locations,
      zones,
      kits,
      cameras,
      ultrasoundDevices,
      sensors,
      species,
      phases,
      projectSettings,
      users
    } satisfies AqlConfigurationDataset;
  }, [dataset, isClientMode, portalAccess, resolvedClientSlug, resolvedProjectSlug]);

  const client = scopedDataset.clients.find((item) =>
    item.client_id === selectedClient?.id ||
    item.slug === selectedClient?.slug ||
    item.name.toLowerCase() === selectedClient?.name?.toLowerCase()
  ) ?? scopedDataset.clients[0] ?? null;
  const clientProjects = client
    ? scopedDataset.projects.filter((item) => item.client_id === client.client_id)
    : scopedDataset.projects;
  const project = resolvedProjectSlug
    ? clientProjects.find((item) => projectMatchesPath(item, resolvedProjectSlug)) ?? clientProjects[0] ?? scopedDataset.projects[0] ?? null
    : clientProjects[0] ?? scopedDataset.projects[0] ?? null;
  const clientLocations = client
    ? scopedDataset.locations.filter((item) => item.client_id === client.client_id)
    : scopedDataset.locations;
  const location = clientLocations.find((item) => item.project_id && item.project_id === project?.project_id)
    ?? clientLocations[0]
    ?? scopedDataset.locations[0]
    ?? null;
  const zone = scopedDataset.zones.find((item) =>
    item.client_id === client?.client_id &&
    (!project?.project_id || item.project_id === project.project_id) &&
    (!location?.location_id || item.location_id === location.location_id)
  ) ?? scopedDataset.zones.find((item) => item.client_id === client?.client_id)
    ?? scopedDataset.zones[0]
    ?? null;
  const phaseOptions = useMemo(() => {
    const phases = isClientMode
      ? dataset.phases.filter((item) =>
        isGlobalPhase(item) ||
        item.client_id === client?.client_id ||
        item.project_id === project?.project_id
      )
      : scopedDataset.phases;

    return buildPhaseSelectOptions(phases, [
      project?.default_phase,
      ...scopedDataset.zones.map((item) => item.phase)
    ]);
  }, [client?.client_id, dataset.phases, isClientMode, project?.default_phase, project?.project_id, scopedDataset.phases, scopedDataset.zones]);

  const clientOptions = scopedDataset.clients.map((item) => ({ value: item.client_id, label: item.name }));
  const getProjectsForClient = (clientId: unknown) => {
    const resolvedClientId = toId(clientId);
    if (!resolvedClientId) return scopedDataset.projects;
    return scopedDataset.projects.filter((item) => item.client_id === resolvedClientId);
  };
  const getFirstProjectForClient = (clientId: unknown) => getProjectsForClient(clientId)[0] ?? null;
  const contextProfileForProjectId = (projectId: unknown) => {
    const linkedProject = scopedDataset.projects.find((item) => item.project_id === toId(projectId));
    return getOperationalContextProfile(linkedProject?.context_type);
  };
  const getLocationsForScope = (clientId: unknown, projectId: unknown) => {
    const resolvedClientId = toId(clientId);
    const resolvedProjectId = toId(projectId);

    return scopedDataset.locations.filter((item) => {
      if (resolvedClientId && item.client_id !== resolvedClientId) return false;
      if (resolvedProjectId && item.project_id && item.project_id !== resolvedProjectId) return false;
      return true;
    });
  };
  const getFirstLocationForScope = (clientId: unknown, projectId: unknown) =>
    getLocationsForScope(clientId, projectId)[0] ?? getLocationsForScope(clientId, '')[0] ?? null;
  const getZonesForScope = (clientId: unknown, projectId: unknown, locationId: unknown) => {
    const resolvedClientId = toId(clientId);
    const resolvedProjectId = toId(projectId);
    const resolvedLocationId = toId(locationId);

    return scopedDataset.zones.filter((item) => {
      if (resolvedClientId && item.client_id !== resolvedClientId) return false;
      if (resolvedProjectId && item.project_id && item.project_id !== resolvedProjectId) return false;
      if (resolvedLocationId && item.location_id !== resolvedLocationId) return false;
      return true;
    });
  };
  const getFirstZoneForScope = (clientId: unknown, projectId: unknown, locationId: unknown) =>
    getZonesForScope(clientId, projectId, locationId)[0] ?? null;
  const getKitsForScope = (clientId: unknown, projectId: unknown, locationId: unknown, zoneId?: unknown) => {
    const resolvedClientId = toId(clientId);
    const resolvedProjectId = toId(projectId);
    const resolvedLocationId = toId(locationId);
    const resolvedZoneId = toId(zoneId);

    return scopedDataset.kits.filter((item) => {
      if (resolvedClientId && item.client_id !== resolvedClientId) return false;
      if (resolvedProjectId && item.project_id !== resolvedProjectId) return false;
      if (resolvedLocationId && item.location_id !== resolvedLocationId) return false;
      if (resolvedZoneId && item.zone_id && item.zone_id !== resolvedZoneId) return false;
      return true;
    });
  };
  const getFirstKitForScope = (clientId: unknown, projectId: unknown, locationId: unknown, zoneId?: unknown) =>
    getKitsForScope(clientId, projectId, locationId, zoneId)[0] ?? null;
  const formatLocationLabel = (item: AqlLocation) =>
    [item.name, item.city, item.country].filter(Boolean).join(' / ') || item.name;
  const getPrimaryProjectForClient = (clientId: unknown) =>
    scopedDataset.projects.find((item) => item.client_id === toId(clientId)) ?? null;
  const getPrimaryLocationForClient = (clientId: unknown) => {
    const primaryProject = getPrimaryProjectForClient(clientId);
    return getFirstLocationForScope(clientId, primaryProject?.project_id ?? '') ?? null;
  };
  const getScopeFromLocation = (locationId: unknown, fallbackProjectId?: unknown) => {
    const selectedLocation = scopedDataset.locations.find((item) => item.location_id === toId(locationId)) ?? null;
    if (!selectedLocation) return {};

    const fallbackProject = scopedDataset.projects.find((item) =>
      item.project_id === toId(fallbackProjectId) &&
      item.client_id === selectedLocation.client_id
    ) ?? null;
    const selectedProject = selectedLocation.project_id
      ? scopedDataset.projects.find((item) =>
        item.project_id === selectedLocation.project_id &&
        item.client_id === selectedLocation.client_id
      ) ?? null
      : fallbackProject ?? getFirstProjectForClient(selectedLocation.client_id);

    return {
      client_id: selectedLocation.client_id,
      project_id: selectedProject?.project_id ?? ''
    };
  };
  const normalizeZoneScope = (item: Record<string, any>) => {
    const selectedLocation = scopedDataset.locations.find((entry) => entry.location_id === item.location_id) ?? null;
    if (!selectedLocation) {
      return item;
    }

    const scopedProject = selectedLocation.project_id
      ? scopedDataset.projects.find((entry) =>
        entry.project_id === selectedLocation.project_id &&
        entry.client_id === selectedLocation.client_id
      ) ?? null
      : scopedDataset.projects.find((entry) =>
        entry.project_id === item.project_id &&
        entry.client_id === selectedLocation.client_id
      ) ?? getFirstProjectForClient(selectedLocation.client_id);

    return {
      ...item,
      client_id: selectedLocation.client_id,
      project_id: scopedProject?.project_id ?? ''
    };
  };
  const getScopeFromZone = (zoneId: unknown) => {
    const selectedZone = scopedDataset.zones.find((item) => item.zone_id === toId(zoneId)) ?? null;
    if (!selectedZone) return {};

    return {
      client_id: selectedZone.client_id,
      project_id: selectedZone.project_id,
      location_id: selectedZone.location_id
    };
  };
  const getScopeFromKit = (kitId: unknown) => {
    const selectedKit = scopedDataset.kits.find((item) => item.kit_id === toId(kitId)) ?? null;
    if (!selectedKit) return {};

    return {
      client_id: selectedKit.client_id,
      project_id: selectedKit.project_id,
      location_id: selectedKit.location_id,
      zone_id: selectedKit.zone_id ?? ''
    };
  };
  const getScopeFromSensor = (sensorId: unknown) => {
    const selectedSensor = scopedDataset.sensors.find((item) => item.sensor_id === toId(sensorId)) ?? null;
    if (!selectedSensor) return {};

    return getScopeFromKit(selectedSensor.kit_id);
  };
  const getSensorsForScope = (clientId: unknown, projectId: unknown) => {
    const resolvedClientId = toId(clientId);
    const resolvedProjectId = toId(projectId);
    const scopedKitIds = new Set(scopedDataset.kits.filter((item) => {
      if (resolvedClientId && item.client_id !== resolvedClientId) return false;
      if (resolvedProjectId && item.project_id !== resolvedProjectId) return false;
      return true;
    }).map((item) => item.kit_id));

    return scopedDataset.sensors.filter((item) => scopedKitIds.has(item.kit_id));
  };
  const getRuleSensorId = (item: Record<string, any>) =>
    String(item.sensor_id ?? item.value?.sensor_id ?? item.value?.sensorId ?? item.value?.source_sensor_id ?? '').trim();
  const getAssignableScope = (item: { kit_id?: string | null; zone_id?: string | null }) => {
    const selectedKit = scopedDataset.kits.find((entry) => entry.kit_id === toId(item.kit_id)) ?? null;
    const selectedZone = scopedDataset.zones.find((entry) =>
      entry.zone_id === toId(item.zone_id) ||
      (selectedKit?.zone_id ? entry.zone_id === selectedKit.zone_id : false)
    ) ?? null;

    return {
      client_id: selectedKit?.client_id ?? selectedZone?.client_id ?? '',
      project_id: selectedKit?.project_id ?? selectedZone?.project_id ?? '',
      location_id: selectedKit?.location_id ?? selectedZone?.location_id ?? '',
      zone_id: item.zone_id ?? selectedKit?.zone_id ?? ''
    };
  };
  const enrichAssignableScope = <T extends { kit_id?: string | null; zone_id?: string | null }>(item: T) => ({
    ...item,
    ...getAssignableScope(item)
  });
  const normalizeKitScope = (item: Record<string, any>) => {
    const status = String(item.status || 'active');
    const normalizedItem = {
      ...item,
      status,
      is_active: status !== 'inactive'
    };

    const selectedZone = scopedDataset.zones.find((entry) => entry.zone_id === item.zone_id) ?? null;
    if (selectedZone) {
      return {
        ...normalizedItem,
        client_id: selectedZone.client_id,
        project_id: selectedZone.project_id,
        location_id: selectedZone.location_id
      };
    }

    const selectedLocation = scopedDataset.locations.find((entry) => entry.location_id === item.location_id) ?? null;
    if (selectedLocation) {
      const scopedProject = selectedLocation.project_id
        ? scopedDataset.projects.find((entry) =>
          entry.project_id === selectedLocation.project_id &&
          entry.client_id === selectedLocation.client_id
        ) ?? null
        : scopedDataset.projects.find((entry) =>
          entry.project_id === item.project_id &&
          entry.client_id === selectedLocation.client_id
        ) ?? getFirstProjectForClient(selectedLocation.client_id);

      return {
        ...normalizedItem,
        client_id: selectedLocation.client_id,
        project_id: scopedProject?.project_id ?? ''
      };
    }

    return {
      ...normalizedItem,
      client_id: item.client_id || client?.client_id || ''
    };
  };
  const normalizeCameraForSave = (item: AqlCamera): AqlCamera => {
    const cameraCode = normalizeCameraCode(item.camera_code || item.name || item.camera_id);
    const liveFeedPath = liveFeedPathForCameraCode(cameraCode);
    const scope = getAssignableScope(item);
    const trainingTarget = clampTrainingTarget(item.training_burst_target);
    const trainingBurstPath = trainingBurstPathForCameraCode(cameraCode);

    return {
      ...item,
      kit_id: item.kit_id || null,
      zone_id: item.zone_id || scope.zone_id || null,
      camera_code: cameraCode || null,
      source_type: item.source_type || 'storage',
      source_uri: item.source_uri || liveFeedPath || null,
      live_feed_path: liveFeedPath || null,
      training_burst_available: Boolean(item.training_burst_available),
      training_burst_enabled: false,
      training_burst_target: trainingTarget,
      training_burst_count: 0,
      training_burst_path: trainingBurstPath || null,
      training_burst_started_at: null,
      training_burst_completed_at: null
    };
  };
  const normalizeSensorForSave = (item: Record<string, any>): AqlSensor => {
    const scope = getAssignableScope(item);

    return {
      sensor_id: String(item.sensor_id),
      kit_id: toId(item.kit_id),
      zone_id: item.zone_id || scope.zone_id || null,
      device_id: item.device_id || null,
      sensor_type: String(item.sensor_type ?? ''),
      name: String(item.name ?? ''),
      unit: item.unit || null,
      min_threshold: item.min_threshold ?? null,
      max_threshold: item.max_threshold ?? null,
      recommended_min: item.recommended_min ?? null,
      recommended_max: item.recommended_max ?? null,
      last_value: item.last_value ?? null,
      is_active: Boolean(item.is_active)
    };
  };
  const normalizeUltrasoundForSave = (item: Record<string, any>): AqlUltrasoundDevice => {
    const selectedKit = scopedDataset.kits.find((entry) => entry.kit_id === toId(item.kit_id)) ?? null;

    return {
      device_id: String(item.device_id),
      client_id: selectedKit?.client_id ?? toId(item.client_id),
      kit_id: toId(item.kit_id),
      name: String(item.name ?? ''),
      device_type: 'ultrasound',
      status: String(item.status || 'active'),
      description: item.description || null,
      is_active: Boolean(item.is_active)
    };
  };
  const geocodeLocationDraft = async (draft: Record<string, any>) => {
    const country = toQueryPart(draft.country) || 'Portugal';
    const query = [draft.address, draft.city, draft.postal_code, country]
      .map(toQueryPart)
      .filter(Boolean)
      .join(', ');

    if (!query) {
      throw new Error(tr('missingAddress'));
    }

    const buildSearchUrl = (structured: boolean) => {
      const params = new URLSearchParams({
        format: 'jsonv2',
        limit: '5',
        addressdetails: '1',
        countrycodes: 'pt',
        'accept-language': 'pt'
      });

      if (structured) {
        const address = toQueryPart(draft.address);
        if (address) params.set('street', address);
        const city = toQueryPart(draft.city);
        if (city) params.set('city', city);
        const postalCode = toQueryPart(draft.postal_code);
        if (postalCode) params.set('postalcode', postalCode);
        params.set('country', country);
      } else {
        params.set('q', query);
      }

      return `https://nominatim.openstreetmap.org/search?${params.toString()}`;
    };

    const fetchCandidates = async (structured: boolean) => {
      const response = await fetch(buildSearchUrl(structured), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(tr('locationGeocodeFailed'));
      const results = await response.json();
      return Array.isArray(results) ? results : [];
    };

    const candidates = [
      ...(await fetchCandidates(true)),
      ...(await fetchCandidates(false))
    ];
    const normalizeCandidate = (candidate: any) => {
      const latitude = Number(candidate?.lat);
      let longitude = Number(candidate?.lon);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

      const canBeMainlandPortugalLongitude = latitude >= 36.8 && latitude <= 42.3 && longitude > 0 && longitude <= 10;
      if (canBeMainlandPortugalLongitude) longitude = -longitude;

      const inPortugalBounds =
        latitude >= 32 &&
        latitude <= 42.3 &&
        longitude >= -31.5 &&
        longitude <= -6;
      if (!inPortugalBounds) return null;

      const displayName = slugify(String(candidate?.display_name ?? ''));
      const citySlug = slugify(toQueryPart(draft.city));
      const address = candidate?.address ?? {};
      const score =
        Number(candidate?.importance ?? 0) +
        (address.country_code === 'pt' ? 5 : 0) +
        (citySlug && displayName.includes(citySlug) ? 2 : 0);

      return { candidate, latitude, longitude, score };
    };

    const match = candidates
      .map(normalizeCandidate)
      .filter((candidate): candidate is NonNullable<ReturnType<typeof normalizeCandidate>> => Boolean(candidate))
      .sort((a, b) => b.score - a.score)[0];

    if (!match) {
      throw new Error(tr('locationGeocodeFailed'));
    }

    const address = match.candidate.address ?? {};
    return {
      latitude: Number(match.latitude.toFixed(6)),
      longitude: Number(match.longitude.toFixed(6)),
      city: draft.city || address.city || address.town || address.village || address.municipality || '',
      country: draft.country || address.country || '',
      postal_code: draft.postal_code || address.postcode || ''
    };
  };

  const createId = () => crypto.randomUUID();
  const createCameraCode = () => `cam-${createId().slice(0, 8)}`;
  const columnPreferenceUserKey = useMemo(() => {
    const explicitUser = portalAccess?.authUserId || portalAccess?.email;
    if (explicitUser) return encodeURIComponent(explicitUser.toLowerCase());

    if (typeof window === 'undefined') return 'local';

    const localUser =
      window.localStorage.getItem('visionAlertsEmail') ||
      window.localStorage.getItem('aql_user_email') ||
      'local';
    return encodeURIComponent(localUser.toLowerCase());
  }, [portalAccess?.authUserId, portalAccess?.email]);
  const columnPreferenceKeyFor = (tableKey: string) => `aql-config-columns:${columnPreferenceUserKey}:${tableKey}`;

  const clientCatalog = useMemo<ClientScope[]>(
    () => buildClientCatalog(scopedDataset.clients, scopedDataset.projects),
    [scopedDataset.clients, scopedDataset.projects]
  );

  useEffect(() => {
    if (!clientCatalog.length) return;
    setAvailableClients(clientCatalog);
    setClient((current) =>
      clientCatalog.find((item) =>
        item.id === current?.id ||
        item.slug === current?.slug ||
        item.name.toLowerCase() === current?.name?.toLowerCase()
      ) ?? clientCatalog[0]
    );
  }, [clientCatalog, selectedClient?.id, selectedClient?.name, selectedClient?.slug, setAvailableClients, setClient]);

  const availableSections = useMemo(() => {
    const mvpSections: SectionId[] = isClientMode
      ? ['client', 'projects', 'locations', 'zones', 'kits', 'cameras', 'ultrasound', 'sensors', 'thresholds', 'vision-copilot', 'users', 'personalization']
      : ['client', 'projects', 'locations', 'zones', 'kits', 'cameras', 'ultrasound', 'sensors', 'thresholds', 'vision-copilot', 'users', 'personalization'];
    const sectionCopy: Partial<Record<SectionId, Pick<ConfigurationSectionDefinition, 'label' | 'title' | 'subtitle'>>> = {
      client: {
        label: tr('sectionClientLabel'),
        title: tr('sectionClientLabel'),
        subtitle: tr('sectionClientSubtitle')
      },
      projects: {
        label: tr('sectionProjectsLabel'),
        title: tr('sectionProjectsLabel'),
        subtitle: 'Projetos operacionais por cliente, incluindo contexto, categoria principal e fase.'
      },
      locations: {
        label: tr('sectionLocationsLabel'),
        title: tr('sectionLocationsLabel'),
        subtitle: tr('sectionLocationsSubtitle')
      },
      zones: {
        label: tr('sectionZonesLabel'),
        title: tr('sectionZonesLabel'),
        subtitle: tr('sectionZonesSubtitle')
      },
      kits: {
        label: tr('sectionKitsLabel'),
        title: tr('sectionKitsLabel'),
        subtitle: tr('sectionKitsSubtitle')
      },
      cameras: {
        label: 'Câmaras',
        title: 'Câmaras',
        subtitle: 'Câmaras, códigos QR e feed live associados às unidades edge.'
      },
      ultrasound: {
        label: 'Sondas',
        title: 'Sondas',
        subtitle: 'Sondas associadas às unidades edge. Sem equipamento ativo, o echogram não aparece no live preview.'
      },
      sensors: {
        label: 'Sensores',
        title: 'Sensores',
        subtitle: tr('sectionSensorsSubtitle')
      },
      thresholds: {
        label: 'Regras de Alertas',
        title: 'Regras de Alertas',
        subtitle: 'Automação que transforma leituras dos sensores em alertas operacionais e casos de validação.'
      },
      'vision-copilot': {
        label: 'Vision Copilot',
        title: 'Vision Copilot',
        subtitle: 'Modo Sentinela, AI multimodal, prompt, limites de token e custos por projeto.'
      },
      users: {
        label: 'Utilizadores',
        title: 'Utilizadores e permissões',
        subtitle: 'Perfis ativos que podem operar a plataforma e classificar alertas.'
      },
      personalization: {
        label: 'Personalização',
        title: 'Personalização',
        subtitle: 'Tema, idioma e ordenação do menu lateral. Preferências pessoais de interface.'
      }
    };

    return CONFIGURATION_SECTIONS
      .filter((item) => mvpSections.includes(item.id))
      .map((item) => ({ ...item, ...sectionCopy[item.id] }));
  }, [isClientMode, lang]);

  useEffect(() => {
    if (availableSections.some((item) => item.id === activeSection)) return;
    setActiveSection(availableSections[0]?.id ?? 'client');
  }, [activeSection, availableSections]);

  const section = useMemo(
    () => availableSections.find((item) => item.id === activeSection) ?? availableSections[0] ?? CONFIGURATION_SECTIONS[0],
    [activeSection, availableSections]
  );
  const standaloneSections = useMemo(
    () => STANDALONE_SECTION_IDS
      .map((sectionId) => availableSections.find((item) => item.id === sectionId))
      .filter(Boolean) as ConfigurationSectionDefinition[],
    [availableSections]
  );

  const handleSave = <K extends keyof AqlConfigurationDataset>(key: K, idKey: string) =>
    async (item: AqlConfigurationDataset[K][number], mode: 'create' | 'edit') => {
      const saved = await saveConfigurationRecord(key, normalizeRecordForSave(key, item));
      setDataset((prev) => {
        const list = [...prev[key]] as Array<Record<string, any>>;
        if (mode === 'create') {
          list.unshift(saved as Record<string, any>);
        } else {
          const index = list.findIndex((entry) => entry[idKey] === (saved as Record<string, any>)[idKey]);
          if (index >= 0) list[index] = saved as Record<string, any>;
          else list.unshift(saved as Record<string, any>);
        }
        return { ...prev, [key]: list } as AqlConfigurationDataset;
      });
    };

  const refreshDatasetFromServer = async () => {
    const result = await loadConfigurationDataset(portalAccess?.accessToken);
    setDataset(result.data);
  };

  const handleDelete = <K extends keyof AqlConfigurationDataset>(key: K, idKey: string) =>
    async (item: AqlConfigurationDataset[K][number]) => {
      if (key === 'clients') {
        const clientToDelete = item as AqlClient;
        await deleteClientCascade(clientToDelete);
        setDataset((prev) => {
          const clientId = clientToDelete.client_id;
          const projectIds = new Set(prev.projects.filter((entry) => entry.client_id === clientId).map((entry) => entry.project_id));
          const locationIds = new Set(prev.locations.filter((entry) => entry.client_id === clientId || projectIds.has(entry.project_id)).map((entry) => entry.location_id));
          const zoneIds = new Set(prev.zones.filter((entry) =>
            entry.client_id === clientId ||
            projectIds.has(entry.project_id) ||
            locationIds.has(entry.location_id)
          ).map((entry) => entry.zone_id));
          const kitIds = new Set(prev.kits.filter((entry) =>
            entry.client_id === clientId ||
            projectIds.has(entry.project_id) ||
            locationIds.has(entry.location_id) ||
            (entry.zone_id ? zoneIds.has(entry.zone_id) : false)
          ).map((entry) => entry.kit_id));

          return {
            ...prev,
            clients: prev.clients.filter((entry) => entry.client_id !== clientId),
            projects: prev.projects.filter((entry) => entry.client_id !== clientId),
            locations: prev.locations.filter((entry) => entry.client_id !== clientId && !locationIds.has(entry.location_id)),
            zones: prev.zones.filter((entry) => entry.client_id !== clientId && !zoneIds.has(entry.zone_id)),
            kits: prev.kits.filter((entry) => entry.client_id !== clientId && !kitIds.has(entry.kit_id)),
            cameras: prev.cameras.filter((entry) =>
              !(entry.kit_id && kitIds.has(entry.kit_id)) &&
              !(entry.zone_id && zoneIds.has(entry.zone_id))
            ),
            ultrasoundDevices: prev.ultrasoundDevices.filter((entry) => entry.client_id !== clientId && !kitIds.has(entry.kit_id)),
            sensors: prev.sensors.filter((entry) =>
              !kitIds.has(entry.kit_id) &&
              !(entry.zone_id && zoneIds.has(entry.zone_id))
            ),
            species: prev.species.filter((entry) => entry.client_id !== clientId),
            phases: prev.phases.filter((entry) => entry.client_id !== clientId),
            projectSettings: prev.projectSettings.filter((entry) => entry.client_id !== clientId),
            users: prev.users.filter((entry) => entry.client_id !== clientId)
          };
        });
        toast.success(`Cliente "${clientToDelete.name}" apagado com dados relacionados.`);
        return;
      }

      if (key === 'projects') {
        const projectToDelete = item as AqlProject;
        await deleteProjectCascade(projectToDelete);
        setDataset((prev) => {
          const projectId = projectToDelete.project_id;
          const locationIds = new Set(prev.locations.filter((entry) => entry.project_id === projectId).map((entry) => entry.location_id));
          const zoneIds = new Set(prev.zones.filter((entry) => entry.project_id === projectId || locationIds.has(entry.location_id)).map((entry) => entry.zone_id));
          const kitIds = new Set(prev.kits.filter((entry) =>
            entry.project_id === projectId ||
            locationIds.has(entry.location_id) ||
            (entry.zone_id ? zoneIds.has(entry.zone_id) : false)
          ).map((entry) => entry.kit_id));

          return {
            ...prev,
            projects: prev.projects.filter((entry) => entry.project_id !== projectId),
            locations: prev.locations.filter((entry) => entry.project_id !== projectId && !locationIds.has(entry.location_id)),
            zones: prev.zones.filter((entry) => entry.project_id !== projectId && !zoneIds.has(entry.zone_id)),
            kits: prev.kits.filter((entry) => entry.project_id !== projectId && !kitIds.has(entry.kit_id)),
            cameras: prev.cameras.filter((entry) =>
              !(entry.kit_id && kitIds.has(entry.kit_id)) &&
              !(entry.zone_id && zoneIds.has(entry.zone_id))
            ),
            ultrasoundDevices: prev.ultrasoundDevices.filter((entry) => !kitIds.has(entry.kit_id)),
            sensors: prev.sensors.filter((entry) =>
              !kitIds.has(entry.kit_id) &&
              !(entry.zone_id && zoneIds.has(entry.zone_id))
            ),
            species: prev.species.filter((entry) => entry.project_id !== projectId),
            phases: prev.phases.filter((entry) => entry.project_id !== projectId),
            projectSettings: prev.projectSettings.filter((entry) => entry.project_id !== projectId),
            users: prev.users.filter((entry) => entry.project_id !== projectId)
          };
        });
        toast.success(`Projeto "${projectToDelete.name}" apagado com dados relacionados.`);
        return;
      }

      if (key === 'locations') {
        await deleteLocationCascade(item as AqlLocation);
        await refreshDatasetFromServer();
        toast.success('Instalação apagada com dados relacionados.');
        return;
      }

      if (key === 'zones') {
        await deleteZoneCascade(item as AqlZone);
        await refreshDatasetFromServer();
        toast.success('Área operacional apagada com dados relacionados.');
        return;
      }

      if (key === 'kits') {
        await deleteKitCascade(item as AqlKit);
        await refreshDatasetFromServer();
        toast.success('Edge apagado com feeds e dados relacionados.');
        return;
      }

      if (key === 'cameras') {
        await deleteCameraCascade(item as AqlCamera);
        await refreshDatasetFromServer();
        toast.success('Câmara apagada com dados relacionados.');
        return;
      }

      if (key === 'sensors') {
        await deleteSensorCascade(item as AqlSensor);
        await refreshDatasetFromServer();
        toast.success('Sensor apagado com dados relacionados.');
        return;
      }

      if (key === 'ultrasoundDevices') {
        await deleteUltrasoundDeviceCascade(item as AqlUltrasoundDevice);
        await refreshDatasetFromServer();
        toast.success('Sonda apagada com dados relacionados.');
        return;
      }

      await deleteConfigurationRecord(key, item);
      setDataset((prev) => {
        const list = (prev[key] as Array<Record<string, any>>).filter((entry) => entry[idKey] !== (item as Record<string, any>)[idKey]);
        return { ...prev, [key]: list } as AqlConfigurationDataset;
      });
      toast.success('Registo apagado.');
    };

  const handleUserSave = async (item: AqlUserAccess, mode: 'create' | 'edit') => {
    if (!portalAccess?.accessToken) {
      throw new Error('Portal session is missing the Supabase access token. Please sign in again.');
    }

    const selectedClientIds = isClientMode
      ? [client?.client_id ?? item.client_id].filter(Boolean).map(String)
      : Array.from(new Set([
          ...((item.client_ids ?? []).map(String).filter(Boolean)),
          ...(item.client_id ? [item.client_id] : [])
        ]));
    const scopeClientId = selectedClientIds[0] ?? item.client_id;
    const isProfileSelfEdit = isClientMode && matchesPortalUser(item, portalAccess);
    const lockedProjectIds = isClientMode
      ? Array.from(new Set([
          ...((item.project_ids ?? []).map(String).filter(Boolean)),
          ...(item.project_id ? [item.project_id] : []),
          ...(resolvedProjectSlug && project?.project_id ? [project.project_id] : [])
        ]))
      : null;
    const projectIds = lockedProjectIds ?? (item.project_scope === 'all'
      ? []
      : Array.from(new Set((item.project_ids ?? []).map(String).filter(Boolean))));
    const saved = await saveConfigurationUserAccess({
      ...item,
      client_id: scopeClientId,
      client_ids: selectedClientIds,
      project_ids: projectIds,
      project_id: projectIds[0] ?? null,
      project_scope: projectIds.length ? 'specific' : 'all',
      role: isProfileSelfEdit ? item.role : item.role,
      is_active: isProfileSelfEdit ? item.is_active : item.is_active
    }, mode, portalAccess);
    setDataset((prev) => {
      const list = [...prev.users];
      if (mode === 'create') {
        list.unshift(saved);
      } else {
        const index = list.findIndex((entry) => entry.user_id === saved.user_id);
        if (index >= 0) list[index] = saved;
        else list.unshift(saved);
      }
      return { ...prev, users: list };
    });
  };

  const clientFields = makeFieldsReadOnly([
    { key: 'client_id', label: tr('clientId'), required: true, hidden: true },
    { key: 'name', label: tr('clientName'), required: true },
    { key: 'slug', label: tr('slugUrl'), required: true, helperText: tr('clientSlugHelper') },
    { key: 'legal_name', label: tr('location') },
    { key: 'logo_url', label: 'Logo URL', hidden: true },
    { key: 'primary_color', label: 'Cor principal', hidden: true },
    { key: 'secondary_color', label: 'Cor secundária', hidden: true },
    { key: 'timezone', label: tr('timezone') },
    { key: 'language', label: tr('language') },
    { key: 'is_active', label: tr('activeInactive'), type: 'status' }
  ], ['client_id']);

  const projectFields = hideFields(makeFieldsReadOnly([
    { key: 'project_id', label: tr('projectId'), required: true, hidden: true },
    { key: 'client_id', label: tr('client'), required: true, type: 'select', options: scopedDataset.clients.map((item) => ({ value: item.client_id, label: item.name })) },
    { key: 'name', label: tr('projectName'), required: true },
    { key: 'slug', label: tr('slugUrl'), required: true, helperText: 'Código usado no acesso ao projeto. Exemplo: /aql/inspecao-linha-1' },
    {
      key: 'context_type',
      label: 'Contexto operacional',
      required: true,
      type: 'select',
      options: OPERATIONAL_CONTEXT_OPTIONS,
      onValueChange: () => ({ species_focus: '', default_phase: '' }),
      helperText: 'Adapta a terminologia e os fluxos do AQL Vision ao domínio deste projeto.'
    },
    { key: 'description', label: tr('description'), type: 'textarea' },
    {
      key: 'cover_image_url',
      label: 'Imagem de capa',
      type: 'image',
      helperText: 'Imagem editorial apresentada no cabeçalho do cartão do projeto. Formatos JPG, PNG ou WebP; máximo 8 MB.',
      uploadImage: uploadProjectCover,
      removeImage: removeProjectCover
    },
    {
      key: 'species_focus',
      label: tr('species'),
      getLabel: (draft) => getOperationalContextProfile(draft.context_type).categoryLabel
    },
    {
      key: 'default_phase',
      label: tr('defaultPhase'),
      getLabel: (draft) => getOperationalContextProfile(draft.context_type).phaseLabel,
      type: 'select',
      getOptions: (draft) => {
        const profile = getOperationalContextProfile(draft.context_type);
        return profile.type === 'aquaculture' ? phaseOptions : profile.phaseOptions;
      }
    },
    { key: 'start_date', label: tr('startDate'), type: 'date' },
    { key: 'end_date', label: tr('endDate'), type: 'date' },
    { key: 'is_active', label: tr('state'), type: 'status' }
  ], ['project_id', ...(isClientMode ? ['client_id'] : [])]), isClientMode ? ['client_id'] : []);

  const locationFields = hideFields(makeFieldsReadOnly([
    { key: 'location_id', label: tr('locationId'), required: true, hidden: true },
    {
      key: 'client_id',
      label: tr('client'),
      required: true,
      type: 'select',
      options: clientOptions,
      onValueChange: (value) => {
        const nextProject = getFirstProjectForClient(value);
        return {
          project_id: nextProject?.project_id ?? '',
          type: getDefaultOperationalLocationType(nextProject?.context_type)
        };
      }
    },
    {
      key: 'project_id',
      label: tr('project'),
      required: true,
      type: 'select',
      getOptions: (draft) => getProjectsForClient(draft.client_id).map((item) => ({ value: item.project_id, label: item.name })),
      onValueChange: (value, draft) => {
        const selectedProject = scopedDataset.projects.find((item) => item.project_id === toId(value)) ?? null;
        const nextClientId = selectedProject?.client_id ?? toId(draft.client_id);
        return {
          client_id: nextClientId,
          type: getDefaultOperationalLocationType(selectedProject?.context_type)
        };
      }
    },
    { key: 'name', label: tr('locationName'), required: true, maxLength: 180 },
    {
      key: 'type',
      label: tr('locationType'),
      required: true,
      type: 'select',
      getOptions: (draft) => {
        const linkedProject = scopedDataset.projects.find((item) => item.project_id === toId(draft.project_id));
        return getOperationalContextProfile(linkedProject?.context_type).locationTypeOptions;
      }
    },
    {
      key: 'address',
      label: tr('address'),
      action: {
        label: tr('locateAddress'),
        loadingLabel: tr('locatingAddress'),
        successMessage: tr('locationGeocodeSuccess'),
        onClick: geocodeLocationDraft
      }
    },
    { key: 'city', label: tr('city'), maxLength: 120 },
    { key: 'postal_code', label: tr('postalCode'), maxLength: 16, helperText: tr('postalCodeHelper') },
    { key: 'country', label: tr('country'), maxLength: 120 },
    { key: 'latitude', label: tr('latitude'), type: 'number', min: -90, max: 90 },
    { key: 'longitude', label: tr('longitude'), type: 'number', min: -180, max: 180 },
    { key: '__map_preview', label: tr('map'), type: 'map', mapFields: { latitudeKey: 'latitude', longitudeKey: 'longitude' } },
    { key: 'description', label: tr('description'), type: 'textarea' },
    { key: 'is_active', label: tr('status'), type: 'status' }
  ], ['location_id', ...(isClientMode ? ['client_id'] : [])]), isClientMode ? ['client_id'] : []);

  const zoneFields = hideFields(makeFieldsReadOnly([
    { key: 'zone_id', label: tr('tankId'), required: true, hidden: true },
    {
      key: 'client_id',
      label: tr('client'),
      required: true,
      type: 'select',
      options: clientOptions,
      onValueChange: (value) => {
        const nextProject = getFirstProjectForClient(value);
        const nextLocation = getFirstLocationForScope(value, nextProject?.project_id ?? '');
        return {
          project_id: nextProject?.project_id ?? '',
          location_id: nextLocation?.location_id ?? '',
          type: nextProject ? getOperationalContextProfile(nextProject.context_type).areaTypeOptions[0]?.value ?? '' : '',
          species: '',
          phase: ''
        };
      }
    },
    {
      key: 'project_id',
      label: tr('project'),
      required: true,
      type: 'select',
      getOptions: (draft) => getProjectsForClient(draft.client_id).map((item) => ({ value: item.project_id, label: item.name })),
      onValueChange: (value, draft) => {
        const selectedProject = scopedDataset.projects.find((item) => item.project_id === toId(value)) ?? null;
        const nextClientId = selectedProject?.client_id ?? toId(draft.client_id);
        const nextLocation = getFirstLocationForScope(nextClientId, value);
        return {
          client_id: nextClientId,
          location_id: nextLocation?.location_id ?? '',
          type: selectedProject ? getOperationalContextProfile(selectedProject.context_type).areaTypeOptions[0]?.value ?? '' : '',
          species: '',
          phase: ''
        };
      }
    },
    {
      key: 'location_id',
      label: tr('installation'),
      required: true,
      type: 'select',
      getOptions: (draft) => getLocationsForScope(draft.client_id, draft.project_id).map((item) => ({ value: item.location_id, label: formatLocationLabel(item) })),
      onValueChange: (value, draft) => getScopeFromLocation(value, draft.project_id)
    },
    {
      key: 'name',
      label: tr('tankName'),
      getLabel: (draft) => {
        const linkedProject = scopedDataset.projects.find((item) => item.project_id === toId(draft.project_id));
        return `Nome da ${getOperationalContextProfile(linkedProject?.context_type).areaLabel.toLowerCase()}`;
      },
      required: true
    },
    { key: 'code', label: tr('code') },
    {
      key: 'type',
      label: tr('type'),
      required: true,
      type: 'select',
      getOptions: (draft) => contextProfileForProjectId(draft.project_id).areaTypeOptions
    },
    {
      key: 'species',
      label: tr('species'),
      getLabel: (draft) => {
        const linkedProject = scopedDataset.projects.find((item) => item.project_id === toId(draft.project_id));
        return getOperationalContextProfile(linkedProject?.context_type).categoryLabel;
      }
    },
    {
      key: 'phase',
      label: tr('biologicalPhase'),
      getLabel: (draft) => {
        const linkedProject = scopedDataset.projects.find((item) => item.project_id === toId(draft.project_id));
        return getOperationalContextProfile(linkedProject?.context_type).phaseLabel;
      },
      type: 'select',
      getOptions: (draft) => {
        const linkedProject = scopedDataset.projects.find((item) => item.project_id === toId(draft.project_id));
        const profile = getOperationalContextProfile(linkedProject?.context_type);
        return profile.type === 'aquaculture' ? phaseOptions : profile.phaseOptions;
      }
    },
    { key: 'latitude', label: tr('latitude'), type: 'number', min: -90, max: 90 },
    { key: 'longitude', label: tr('longitude'), type: 'number', min: -180, max: 180 },
    { key: '__map_preview', label: tr('map'), type: 'map', mapFields: { latitudeKey: 'latitude', longitudeKey: 'longitude' } },
    { key: 'description', label: tr('observations'), type: 'textarea' },
    { key: 'is_active', label: tr('activeInactive'), type: 'status' }
  ], ['zone_id', ...(isClientMode ? ['client_id'] : [])]), isClientMode ? ['client_id'] : []);

  const kitFields = hideFields(makeFieldsReadOnly([
    { key: 'kit_id', label: 'ID do kit', required: true, hidden: true },
    {
      key: 'client_id',
      label: tr('client'),
      required: true,
      type: 'select',
      options: clientOptions,
      onValueChange: (value) => {
        if (!toId(value)) {
          return {
            project_id: '',
            location_id: '',
            zone_id: ''
          };
        }

        const nextProject = getFirstProjectForClient(value);
        const nextLocation = getFirstLocationForScope(value, nextProject?.project_id ?? '');
        const nextZone = getFirstZoneForScope(value, nextProject?.project_id ?? '', nextLocation?.location_id ?? '');
        return {
          project_id: nextProject?.project_id ?? '',
          location_id: nextLocation?.location_id ?? '',
          zone_id: nextZone?.zone_id ?? ''
        };
      }
    },
    {
      key: 'project_id',
      label: 'Projeto',
      required: true,
      type: 'select',
      getOptions: (draft) => getProjectsForClient(draft.client_id).map((item) => ({ value: item.project_id, label: item.name })),
      onValueChange: (value, draft) => {
        if (!toId(value)) {
          return {
            location_id: '',
            zone_id: ''
          };
        }

        const selectedProject = scopedDataset.projects.find((item) => item.project_id === toId(value)) ?? null;
        const nextClientId = selectedProject?.client_id ?? toId(draft.client_id);
        const nextLocation = getFirstLocationForScope(nextClientId, value);
        const nextZone = getFirstZoneForScope(nextClientId, value, nextLocation?.location_id ?? '');
        return {
          client_id: nextClientId,
          location_id: nextLocation?.location_id ?? '',
          zone_id: nextZone?.zone_id ?? ''
        };
      }
    },
    {
      key: 'location_id',
      label: 'Instalação',
      required: true,
      type: 'select',
      getOptions: (draft) => getLocationsForScope(draft.client_id, draft.project_id).map((item) => ({ value: item.location_id, label: formatLocationLabel(item) })),
      onValueChange: (value, draft) => {
        if (!toId(value)) {
          return { zone_id: '' };
        }

        const scope = getScopeFromLocation(value, draft.project_id);
        const nextClientId = scope.client_id ?? draft.client_id;
        const nextProjectId = scope.project_id ?? draft.project_id;
        const nextZone = getFirstZoneForScope(nextClientId, nextProjectId, value);
        return {
          ...scope,
          zone_id: nextZone?.zone_id ?? ''
        };
      }
    },
    {
      key: 'zone_id',
      label: tr('associatedTank'),
      getLabel: (draft) => contextProfileForProjectId(draft.project_id).areaLabel,
      type: 'select',
      getOptions: (draft) => getZonesForScope(draft.client_id, draft.project_id, draft.location_id).map((item) => ({ value: item.zone_id, label: item.name })),
      onValueChange: (value) => getScopeFromZone(value)
    },
    { key: 'name', label: 'Nome da unidade', required: true, maxLength: 180 },
    { key: 'kit_type', label: 'Capacidades da unidade edge', required: true, type: 'select', options: [{ value: 'vision', label: 'Visão computacional' }, { value: 'iot', label: 'Sensores e automação' }, { value: 'hybrid', label: 'Visão + sensores e automação' }] },
    { key: 'controller_profile', label: 'Controlador', required: true, type: 'select', options: [{ value: 'atronia-atlas', label: 'ATronia Atlas' }, { value: 'generic', label: 'Controlador compatível / genérico' }] },
    { key: 'connectivity_mode', label: 'Ligação principal', required: true, type: 'select', options: [{ value: 'ethernet', label: 'Ethernet' }, { value: 'wifi', label: 'Wi-Fi' }, { value: 'cellular', label: 'Rede móvel' }, { value: 'ethernet_cellular', label: 'Ethernet + rede móvel de reserva' }] },
    { key: 'offline_buffer_hours', label: 'Autonomia de dados offline (horas)', type: 'number', min: 1, max: 8760, helperText: 'Período mínimo que o controlador deve conservar leituras sem Internet.' },
    { key: 'bulk_batch_size', label: 'Leituras por lote', type: 'number', min: 1, max: 5000, helperText: 'Quantidade máxima enviada em cada lote após recuperar a ligação.' },
    { key: 'local_alerting_enabled', label: 'Alertas e automação local', type: 'toggle', helperText: 'Mantém regras críticas ativas no cliente mesmo sem ligação à cloud.' },
    { key: 'configuration_revision', label: 'Revisão da configuração', type: 'number', min: 1, readOnly: true },
    { key: 'status', label: 'Estado da unidade', required: true, type: 'select', options: [{ value: 'active', label: 'ativo' }, { value: 'inactive', label: 'inativo' }, { value: 'maintenance', label: 'manutenção' }] },
    { key: 'description', label: 'Observações', type: 'textarea' },
    {
      key: 'capture_interval_seconds',
      label: 'Intervalo de captura (s)',
      type: 'number',
      min: 1,
      max: 86400,
      helperText: 'Intervalo base de aquisição enviado ao agente edge.'
    },
    {
      key: 'frame_interval_ms',
      label: 'Intervalo live (ms)',
      type: 'number',
      min: 50,
      max: 60000,
      helperText: 'Intervalo usado pela unidade edge para publicar frames live. Ex.: 1000 = 1 frame por segundo.'
    },
    { key: 'storage_bucket', label: 'Bucket de imagens', helperText: 'Bucket Supabase usado pelo agente. Normalmente: captures.' },
    { key: 'jpg_quality', label: 'Qualidade JPEG (%)', type: 'number', min: 30, max: 95, helperText: 'Qualidade de compressão das imagens enviadas pelo Raspberry.' },
    { key: 'live_frame_path_template', label: 'Modelo do caminho live', helperText: 'Aceita {camera_code} e {camera_id}. Ex.: live/{camera_code}/feed.jpg.' }
  ], ['kit_id', ...(isClientMode ? ['client_id'] : [])]), isClientMode ? ['client_id'] : []);

  const cameraFields = hideFields(makeFieldsReadOnly([
    { key: 'camera_id', label: 'Camera ID', required: true, hidden: true },
    {
      key: 'client_id',
      label: tr('client'),
      required: true,
      type: 'select',
      options: clientOptions,
      onValueChange: (value) => {
        if (!toId(value)) {
          return {
            project_id: '',
            location_id: '',
            zone_id: '',
            kit_id: ''
          };
        }

        const nextProject = getFirstProjectForClient(value);
        const nextLocation = getFirstLocationForScope(value, nextProject?.project_id ?? '');
        const nextZone = getFirstZoneForScope(value, nextProject?.project_id ?? '', nextLocation?.location_id ?? '');
        return {
          project_id: nextProject?.project_id ?? '',
          location_id: nextLocation?.location_id ?? '',
          zone_id: nextZone?.zone_id ?? '',
          kit_id: ''
        };
      }
    },
    {
      key: 'project_id',
      label: tr('project'),
      required: true,
      type: 'select',
      getOptions: (draft) => getProjectsForClient(draft.client_id).map((item) => ({ value: item.project_id, label: item.name })),
      onValueChange: (value, draft) => {
        if (!toId(value)) {
          return {
            location_id: '',
            zone_id: '',
            kit_id: ''
          };
        }

        const selectedProject = scopedDataset.projects.find((item) => item.project_id === toId(value)) ?? null;
        const nextClientId = selectedProject?.client_id ?? toId(draft.client_id);
        const nextLocation = getFirstLocationForScope(nextClientId, value);
        const nextZone = getFirstZoneForScope(nextClientId, value, nextLocation?.location_id ?? '');
        return {
          client_id: nextClientId,
          location_id: nextLocation?.location_id ?? '',
          zone_id: nextZone?.zone_id ?? '',
          kit_id: ''
        };
      }
    },
    {
      key: 'location_id',
      label: tr('installation'),
      required: true,
      type: 'select',
      getOptions: (draft) => getLocationsForScope(draft.client_id, draft.project_id).map((item) => ({ value: item.location_id, label: formatLocationLabel(item) })),
      onValueChange: (value, draft) => {
        if (!toId(value)) {
          return {
            zone_id: '',
            kit_id: ''
          };
        }

        const scope = getScopeFromLocation(value, draft.project_id);
        const nextClientId = scope.client_id ?? draft.client_id;
        const nextProjectId = scope.project_id ?? draft.project_id;
        const nextZone = getFirstZoneForScope(nextClientId, nextProjectId, value);
        return {
          ...scope,
          zone_id: nextZone?.zone_id ?? '',
          kit_id: ''
        };
      }
    },
    {
      key: 'zone_id',
      label: tr('associatedTank'),
      getLabel: (draft) => contextProfileForProjectId(draft.project_id).areaLabel,
      required: true,
      type: 'select',
      getOptions: (draft) => getZonesForScope(draft.client_id, draft.project_id, draft.location_id).map((item) => ({ value: item.zone_id, label: item.name })),
      onValueChange: (value, draft) => {
        if (!toId(value)) {
          return { kit_id: '' };
        }

        const scope = getScopeFromZone(value);
        const nextClientId = scope.client_id ?? draft.client_id;
        const nextProjectId = scope.project_id ?? draft.project_id;
        const nextLocationId = scope.location_id ?? draft.location_id;
        const currentKit = scopedDataset.kits.find((item) => item.kit_id === toId(draft.kit_id)) ?? null;
        const currentKitStillValid = currentKit
          ? getKitsForScope(nextClientId, nextProjectId, nextLocationId, value).some((item) => item.kit_id === currentKit.kit_id)
          : false;
        return {
          ...scope,
          kit_id: currentKitStillValid ? draft.kit_id : ''
        };
      }
    },
    {
      key: 'kit_id',
      label: 'Unidade',
      type: 'select',
      helperText: 'Opcional. Podes associar esta câmara a uma unidade edge mais tarde.',
      getOptions: (draft) => getKitsForScope(draft.client_id, draft.project_id, draft.location_id, draft.zone_id)
        .map((item) => ({ value: item.kit_id, label: item.name })),
      onValueChange: (value, draft) => {
        if (!toId(value)) return {};
        const scope = getScopeFromKit(value) as Record<string, any>;
        return {
          ...scope,
          zone_id: scope.zone_id || draft.zone_id || ''
        };
      }
    },
    { key: 'name', label: 'Nome da câmara', required: true },
    {
      key: 'camera_code',
      label: 'Código QR / pasta live',
      required: true,
      maxLength: 80,
      helperText: 'Este é o valor a colocar no QR code. O edge device usa-o para gravar em captures/live/<codigo>/feed.jpg.',
      onValueChange: (value, draft) => {
        const cameraCode = normalizeCameraCode(value);
        const liveFeedPath = liveFeedPathForCameraCode(cameraCode);
        const trainingBurstPath = trainingBurstPathForCameraCode(cameraCode);
        return {
          camera_code: cameraCode,
          live_feed_path: liveFeedPath,
          training_burst_path: trainingBurstPath,
          source_uri: draft.source_type === 'storage' || !draft.source_uri ? liveFeedPath : draft.source_uri
        };
      }
    },
    { key: 'live_feed_path', label: 'Caminho live no bucket', helperText: 'Atualizado automaticamente a partir do código QR.' },
    { key: 'source_type', label: 'Tipo de feed', type: 'select', options: [
      { value: 'storage', label: 'Supabase Storage live' },
      { value: 'rtsp', label: 'RTSP' },
      { value: 'snapshot', label: 'HTTP snapshot' },
      { value: 'usb', label: 'USB/local' }
    ], onValueChange: (value, draft) => {
      if (value !== 'storage') return {};
      return { source_uri: draft.live_feed_path || liveFeedPathForCameraCode(draft.camera_code) };
    } },
    { key: 'source_uri', label: 'Source URI / path' },
    {
      key: 'frame_interval_ms',
      label: 'Intervalo live (ms)',
      type: 'number',
      min: 50,
      max: 60000,
      helperText: 'Intervalo usado pelo edge device para esta câmara. Ex.: 1000 = 1 frame por segundo.'
    },
    {
      key: 'training_burst_available',
      label: 'Mostrar botão de treino no live',
      type: 'toggle',
      helperText: 'Quando ativo, aparece no live video o botão para guardar um burst de frames para treino.'
    },
    {
      key: 'training_burst_target',
      label: 'Frames por burst',
      type: 'number',
      min: 1,
      max: 500,
      helperText: 'Por defeito são 50 frames novos por cada clique no live.'
    },
    { key: 'resolution', label: 'Resolution' },
    { key: 'position_description', label: 'Position description', type: 'textarea' },
    { key: 'last_frame_status', label: 'Estado do último frame' },
    { key: 'last_frame_at', label: 'Hora do último frame' },
    { key: 'is_primary', label: 'Primary', type: 'toggle' },
    { key: 'is_active', label: 'Status', type: 'status' }
  ], ['camera_id', 'live_feed_path', 'last_frame_status', 'last_frame_at', ...(isClientMode ? ['client_id'] : [])]), isClientMode ? ['client_id'] : []);

  const ultrasoundFields = hideFields(makeFieldsReadOnly([
    { key: 'device_id', label: 'ID do equipamento', required: true, hidden: true },
    {
      key: 'client_id',
      label: tr('client'),
      required: true,
      type: 'select',
      options: clientOptions,
      onValueChange: (value) => {
        if (!toId(value)) {
          return {
            project_id: '',
            location_id: '',
            zone_id: '',
            kit_id: ''
          };
        }

        const nextProject = getFirstProjectForClient(value);
        const nextLocation = getFirstLocationForScope(value, nextProject?.project_id ?? '');
        const nextZone = getFirstZoneForScope(value, nextProject?.project_id ?? '', nextLocation?.location_id ?? '');
        const nextKit = getFirstKitForScope(value, nextProject?.project_id ?? '', nextLocation?.location_id ?? '', nextZone?.zone_id ?? '');
        return {
          project_id: nextProject?.project_id ?? '',
          location_id: nextLocation?.location_id ?? '',
          zone_id: nextKit?.zone_id ?? nextZone?.zone_id ?? '',
          kit_id: nextKit?.kit_id ?? ''
        };
      }
    },
    {
      key: 'project_id',
      label: tr('project'),
      required: true,
      type: 'select',
      getOptions: (draft) => getProjectsForClient(draft.client_id).map((item) => ({ value: item.project_id, label: item.name })),
      onValueChange: (value, draft) => {
        if (!toId(value)) {
          return {
            location_id: '',
            zone_id: '',
            kit_id: ''
          };
        }

        const selectedProject = scopedDataset.projects.find((item) => item.project_id === toId(value)) ?? null;
        const nextClientId = selectedProject?.client_id ?? toId(draft.client_id);
        const nextLocation = getFirstLocationForScope(nextClientId, value);
        const nextZone = getFirstZoneForScope(nextClientId, value, nextLocation?.location_id ?? '');
        const nextKit = getFirstKitForScope(nextClientId, value, nextLocation?.location_id ?? '', nextZone?.zone_id ?? '');
        return {
          client_id: nextClientId,
          location_id: nextLocation?.location_id ?? '',
          zone_id: nextKit?.zone_id ?? nextZone?.zone_id ?? '',
          kit_id: nextKit?.kit_id ?? ''
        };
      }
    },
    {
      key: 'location_id',
      label: tr('installation'),
      required: true,
      type: 'select',
      getOptions: (draft) => getLocationsForScope(draft.client_id, draft.project_id).map((item) => ({ value: item.location_id, label: formatLocationLabel(item) })),
      onValueChange: (value, draft) => {
        if (!toId(value)) {
          return {
            zone_id: '',
            kit_id: ''
          };
        }

        const scope = getScopeFromLocation(value, draft.project_id);
        const nextClientId = scope.client_id ?? draft.client_id;
        const nextProjectId = scope.project_id ?? draft.project_id;
        const nextZone = getFirstZoneForScope(nextClientId, nextProjectId, value);
        const nextKit = getFirstKitForScope(nextClientId, nextProjectId, value, nextZone?.zone_id ?? '');
        return {
          ...scope,
          zone_id: nextKit?.zone_id ?? nextZone?.zone_id ?? '',
          kit_id: nextKit?.kit_id ?? ''
        };
      }
    },
    {
      key: 'zone_id',
      label: tr('associatedTank'),
      getLabel: (draft) => contextProfileForProjectId(draft.project_id).areaLabel,
      type: 'select',
      getOptions: (draft) => getZonesForScope(draft.client_id, draft.project_id, draft.location_id).map((item) => ({ value: item.zone_id, label: item.name })),
      onValueChange: (value, draft) => {
        if (!toId(value)) {
          return { kit_id: '' };
        }

        const scope = getScopeFromZone(value);
        const nextClientId = scope.client_id ?? draft.client_id;
        const nextProjectId = scope.project_id ?? draft.project_id;
        const nextLocationId = scope.location_id ?? draft.location_id;
        const nextKit = getFirstKitForScope(nextClientId, nextProjectId, nextLocationId, value);
        return {
          ...scope,
          kit_id: nextKit?.kit_id ?? ''
        };
      }
    },
    {
      key: 'kit_id',
      label: 'Unidade',
      required: true,
      type: 'select',
      getOptions: (draft) => getKitsForScope(draft.client_id, draft.project_id, draft.location_id, draft.zone_id)
        .map((item) => ({ value: item.kit_id, label: item.name })),
      onValueChange: (value, draft) => {
        if (!toId(value)) return {};
        const scope = getScopeFromKit(value) as Record<string, any>;
        return {
          ...scope,
          zone_id: scope.zone_id || draft.zone_id || ''
        };
      }
    },
    { key: 'name', label: 'Nome do equipamento', required: true },
    { key: 'status', label: 'Estado', required: true, type: 'select', options: [
      { value: 'active', label: 'ativo' },
      { value: 'inactive', label: 'inativo' },
      { value: 'maintenance', label: 'manutenção' }
    ] },
    { key: 'description', label: 'Observações', type: 'textarea' },
    { key: 'is_active', label: 'Equipamento ativo/inativo', type: 'status' }
  ], ['device_id', ...(isClientMode ? ['client_id'] : [])]), isClientMode ? ['client_id'] : []);

  const sensorFields = hideFields(makeFieldsReadOnly([
    { key: 'sensor_id', label: 'ID do sensor', required: true, hidden: true },
    {
      key: 'client_id',
      label: tr('client'),
      required: true,
      type: 'select',
      options: clientOptions,
      onValueChange: (value) => {
        if (!toId(value)) {
          return {
            project_id: '',
            location_id: '',
            zone_id: '',
            kit_id: ''
          };
        }

        const nextProject = getFirstProjectForClient(value);
        const nextLocation = getFirstLocationForScope(value, nextProject?.project_id ?? '');
        const nextZone = getFirstZoneForScope(value, nextProject?.project_id ?? '', nextLocation?.location_id ?? '');
        const nextKit = getFirstKitForScope(value, nextProject?.project_id ?? '', nextLocation?.location_id ?? '', nextZone?.zone_id ?? '');
        return {
          project_id: nextProject?.project_id ?? '',
          location_id: nextLocation?.location_id ?? '',
          zone_id: nextKit?.zone_id ?? nextZone?.zone_id ?? '',
          kit_id: nextKit?.kit_id ?? ''
        };
      }
    },
    {
      key: 'project_id',
      label: tr('project'),
      required: true,
      type: 'select',
      getOptions: (draft) => getProjectsForClient(draft.client_id).map((item) => ({ value: item.project_id, label: item.name })),
      onValueChange: (value, draft) => {
        if (!toId(value)) {
          return {
            location_id: '',
            zone_id: '',
            kit_id: ''
          };
        }

        const selectedProject = scopedDataset.projects.find((item) => item.project_id === toId(value)) ?? null;
        const nextClientId = selectedProject?.client_id ?? toId(draft.client_id);
        const nextLocation = getFirstLocationForScope(nextClientId, value);
        const nextZone = getFirstZoneForScope(nextClientId, value, nextLocation?.location_id ?? '');
        const nextKit = getFirstKitForScope(nextClientId, value, nextLocation?.location_id ?? '', nextZone?.zone_id ?? '');
        return {
          client_id: nextClientId,
          location_id: nextLocation?.location_id ?? '',
          zone_id: nextKit?.zone_id ?? nextZone?.zone_id ?? '',
          kit_id: nextKit?.kit_id ?? ''
        };
      }
    },
    {
      key: 'location_id',
      label: tr('installation'),
      required: true,
      type: 'select',
      getOptions: (draft) => getLocationsForScope(draft.client_id, draft.project_id).map((item) => ({ value: item.location_id, label: formatLocationLabel(item) })),
      onValueChange: (value, draft) => {
        if (!toId(value)) {
          return {
            zone_id: '',
            kit_id: ''
          };
        }

        const scope = getScopeFromLocation(value, draft.project_id);
        const nextClientId = scope.client_id ?? draft.client_id;
        const nextProjectId = scope.project_id ?? draft.project_id;
        const nextZone = getFirstZoneForScope(nextClientId, nextProjectId, value);
        const nextKit = getFirstKitForScope(nextClientId, nextProjectId, value, nextZone?.zone_id ?? '');
        return {
          ...scope,
          zone_id: nextKit?.zone_id ?? nextZone?.zone_id ?? '',
          kit_id: nextKit?.kit_id ?? ''
        };
      }
    },
    {
      key: 'zone_id',
      label: tr('associatedTank'),
      getLabel: (draft) => contextProfileForProjectId(draft.project_id).areaLabel,
      type: 'select',
      getOptions: (draft) => getZonesForScope(draft.client_id, draft.project_id, draft.location_id).map((item) => ({ value: item.zone_id, label: item.name })),
      onValueChange: (value, draft) => {
        if (!toId(value)) {
          return { kit_id: '' };
        }

        const scope = getScopeFromZone(value);
        const nextClientId = scope.client_id ?? draft.client_id;
        const nextProjectId = scope.project_id ?? draft.project_id;
        const nextLocationId = scope.location_id ?? draft.location_id;
        const nextKit = getFirstKitForScope(nextClientId, nextProjectId, nextLocationId, value);
        return {
          ...scope,
          kit_id: nextKit?.kit_id ?? ''
        };
      }
    },
    {
      key: 'kit_id',
      label: 'Unidade',
      required: true,
      type: 'select',
      getOptions: (draft) => getKitsForScope(draft.client_id, draft.project_id, draft.location_id, draft.zone_id)
        .map((item) => ({ value: item.kit_id, label: item.name })),
      onValueChange: (value, draft) => {
        if (!toId(value)) return {};
        const scope = getScopeFromKit(value) as Record<string, any>;
        return {
          ...scope,
          zone_id: scope.zone_id || draft.zone_id || ''
        };
      }
    },
    { key: 'sensor_type', label: 'Tipo de sensor', required: true, type: 'select', getOptions: (draft) =>
      contextProfileForProjectId(draft.project_id).sensorTypeOptions.map(({ value, label }) => ({ value, label })), onValueChange: (value, draft) => {
      const preset = contextProfileForProjectId(draft.project_id).sensorTypeOptions.find((item) => item.value === String(value));
      return {
        name: preset?.label || '',
        unit: preset?.unit ?? '',
        min_threshold: preset?.min ?? null,
        max_threshold: preset?.max ?? null
      };
    } },
    { key: 'name', label: 'Nome do sensor', required: true },
    { key: 'unit', label: 'Unidade' },
    { key: 'min_threshold', label: 'Mínimo', type: 'number' },
    { key: 'max_threshold', label: 'Máximo', type: 'number' },
    { key: 'recommended_min', label: 'Mínimo recomendado', type: 'number' },
    { key: 'recommended_max', label: 'Máximo recomendado', type: 'number' },
    { key: 'last_value', label: 'Último valor', type: 'number' },
    { key: 'is_active', label: 'Sensor ativo/inativo', type: 'status' }
  ], ['sensor_id', ...(isClientMode ? ['client_id', 'project_id', 'location_id', 'zone_id'] : [])]), isClientMode ? ['client_id', 'project_id', 'location_id', 'zone_id'] : []);

  const speciesFields = hideFields(makeFieldsReadOnly([
    { key: 'species_id', label: 'Species ID', required: true },
    { key: 'client_id', label: 'Client', required: true, type: 'select', options: scopedDataset.clients.map((item) => ({ value: item.client_id, label: item.name })) },
    { key: 'project_id', label: 'Project', required: true, type: 'select', options: scopedDataset.projects.map((item) => ({ value: item.project_id, label: item.name })) },
    { key: 'common_name', label: 'Common name', required: true },
    { key: 'scientific_name', label: 'Scientific name' },
    { key: 'code', label: 'Code' },
    { key: 'is_active', label: 'Status', type: 'status' }
  ], ['species_id', ...(isClientMode ? ['client_id'] : [])]), isClientMode ? ['client_id'] : []);

  const phaseFields = hideFields(makeFieldsReadOnly([
    { key: 'phase_id', label: 'Phase ID', required: true },
    { key: 'client_id', label: 'Client', required: true, type: 'select', options: scopedDataset.clients.map((item) => ({ value: item.client_id, label: item.name })) },
    { key: 'project_id', label: 'Project', required: true, type: 'select', options: scopedDataset.projects.map((item) => ({ value: item.project_id, label: item.name })) },
    { key: 'name', label: 'Phase name', required: true },
    { key: 'code', label: 'Code', required: true },
    { key: 'sort_order', label: 'Sort order', type: 'number' },
    { key: 'is_active', label: 'Status', type: 'status' }
  ], ['phase_id', ...(isClientMode ? ['client_id'] : [])]), isClientMode ? ['client_id'] : []);

  const thresholdFields = hideFields(makeFieldsReadOnly([
    { key: 'setting_id', label: 'Setting ID', required: true },
    { key: 'client_id', label: 'Client', required: true, type: 'select', options: scopedDataset.clients.map((item) => ({ value: item.client_id, label: item.name })) },
    { key: 'project_id', label: 'Project', required: true, type: 'select', options: scopedDataset.projects.map((item) => ({ value: item.project_id, label: item.name })) },
    {
      key: 'sensor_id',
      label: 'Sensor',
      required: true,
      type: 'select',
      helperText: 'A regra operacional deve ficar associada ao sensor que origina a leitura ou alerta.',
      getOptions: (draft) => getSensorsForScope(draft.client_id, draft.project_id).map((item) => {
        const normalizedType = String(item.sensor_type ?? '').toLowerCase();
        const label = sensorLabelMap[normalizedType] ?? item.sensor_type ?? 'Sensor';
        return {
          value: item.sensor_id,
          label: `${item.name || label} · ${label}`
        };
      }),
      onValueChange: (value) => getScopeFromSensor(value)
    },
    { key: 'key', label: 'Key', required: true },
    { key: 'value', label: 'JSON value', required: true, type: 'textarea' }
  ], ['setting_id', ...(isClientMode ? ['client_id'] : [])]), isClientMode ? ['client_id'] : []);

  const copilotSettings = scopedDataset.projectSettings.filter((item) => item.key === VISION_COPILOT_SETTING_KEY);
  const selectedCopilotSetting = copilotSettings.find((item) => item.project_id === project?.project_id) ?? copilotSettings[0] ?? null;
  const copilotItems = (selectedCopilotSetting ? [selectedCopilotSetting] : []).map((item) => {
    const value = item.value ?? {};
    return {
      setting_id: item.setting_id,
      client_id: item.client_id,
      project_id: item.project_id,
      key: item.key,
      sentinel_enabled: Boolean(value.sentinel_enabled ?? false),
      analyze_on_alert: Boolean(value.analyze_on_alert ?? true),
      add_alarm_context: Boolean(value.add_alarm_context ?? true),
      analysis_interval_minutes: Number(value.analysis_interval_minutes ?? 60),
      model: String(value.model ?? ''),
      max_output_tokens: Number(value.max_output_tokens ?? 700),
      monthly_token_budget: Number(value.monthly_token_budget ?? 250000),
      monthly_tokens_used: Number(value.monthly_tokens_used ?? 0),
      cost_per_1k_tokens_eur: Number(value.cost_per_1k_tokens_eur ?? 0),
      estimated_monthly_cost_eur: Number(value.estimated_monthly_cost_eur ?? 0),
      last_sentinel_analysis_at: String(value.last_sentinel_analysis_at ?? ''),
      system_prompt: String(value.system_prompt ?? DEFAULT_COPILOT_PROMPT)
    };
  });
  const copilotFields = hideFields(makeFieldsReadOnly([
    { key: 'setting_id', label: 'Setting ID', required: true, hidden: true },
    { key: 'client_id', label: tr('client'), required: true, type: 'select', options: scopedDataset.clients.map((item) => ({ value: item.client_id, label: item.name })) },
    { key: 'project_id', label: tr('project'), required: true, type: 'select', getOptions: (draft) => getProjectsForClient(draft.client_id).map((item) => ({ value: item.project_id, label: item.name })) },
    { key: 'key', label: 'Key', required: true, hidden: true },
    { key: 'sentinel_enabled', label: 'Ativar Modo Sentinela', type: 'toggle', helperText: 'Quando ativo, o agente pode analisar automaticamente alertas visuais e alertas de sensores conforme os limites abaixo.' },
    { key: 'analyze_on_alert', label: 'Analisar imediatamente quando há alarme', type: 'toggle', helperText: 'Dá prioridade a alertas visuais ou ambientais e depois volta à cadência normal.' },
    { key: 'add_alarm_context', label: 'Adicionar contexto ao receber o alarme', type: 'toggle', helperText: 'Mostra o alerta primeiro e acrescenta parecer IA quando a análise terminar.' },
    { key: 'analysis_interval_minutes', label: 'Cadência normal de análise (min)', type: 'number', min: 5, max: 1440 },
    { key: 'model', label: 'Modelo Gemini', type: 'select', placeholder: 'Automático', options: [
      { value: 'gemini-2.5-flash', label: 'gemini-2.5-flash' },
      { value: 'gemini-2.5-flash-lite', label: 'gemini-2.5-flash-lite' },
      { value: 'gemini-2.0-flash', label: 'gemini-2.0-flash' }
    ] },
    { key: 'max_output_tokens', label: 'Máximo tokens de resposta', type: 'number', min: 200, max: 2000 },
    { key: 'monthly_token_budget', label: 'Orçamento mensal de tokens', type: 'number', min: 0 },
    { key: 'cost_per_1k_tokens_eur', label: 'Custo estimado por 1.000 tokens EUR', type: 'number', min: 0, helperText: 'Parâmetro manual para converter uso de tokens em custo estimado sem depender de preços externos.' },
    { key: 'monthly_tokens_used', label: 'Tokens usados este mês', type: 'number', min: 0, readOnly: true },
    { key: 'estimated_monthly_cost_eur', label: 'Custo estimado mensal EUR', type: 'number', min: 0, readOnly: true },
    { key: 'last_sentinel_analysis_at', label: 'Última análise sentinela', readOnly: true },
    { key: 'system_prompt', label: 'Prompt operacional do Copilot', type: 'textarea', required: true, maxLength: 4000 }
  ], ['setting_id', 'key', 'monthly_tokens_used', 'estimated_monthly_cost_eur', 'last_sentinel_analysis_at', ...(isClientMode ? ['client_id'] : [])]), isClientMode ? ['client_id'] : []);

  const saveCopilotSetting = async (item: Record<string, any>, mode: 'create' | 'edit') => {
    const next = {
      setting_id: String(item.setting_id || createId()),
      client_id: String(item.client_id || client?.client_id || ''),
      project_id: String(item.project_id || project?.project_id || ''),
      key: VISION_COPILOT_SETTING_KEY,
      value: {
        sentinel_enabled: Boolean(item.sentinel_enabled),
        analyze_on_alert: Boolean(item.analyze_on_alert),
        add_alarm_context: Boolean(item.add_alarm_context),
        analysis_interval_minutes: Math.max(5, Math.floor(Number(item.analysis_interval_minutes) || 60)),
        model: String(item.model || 'gemini-2.5-flash'),
        max_output_tokens: Math.max(200, Math.min(2000, Math.floor(Number(item.max_output_tokens) || 700))),
        monthly_token_budget: Math.max(0, Math.floor(Number(item.monthly_token_budget) || 0)),
        monthly_tokens_used: Math.max(0, Math.floor(Number(item.monthly_tokens_used) || 0)),
        cost_per_1k_tokens_eur: Math.max(0, Number(item.cost_per_1k_tokens_eur) || 0),
        estimated_monthly_cost_eur: Math.max(0, Number(item.estimated_monthly_cost_eur) || 0),
        last_sentinel_analysis_at: String(item.last_sentinel_analysis_at || ''),
        system_prompt: String(item.system_prompt || DEFAULT_COPILOT_PROMPT).slice(0, 4000)
      }
    };

    await handleSave('projectSettings', 'setting_id')(next, mode);
  };

  const saveThresholdSetting = async (item: Record<string, any>, mode: 'create' | 'edit') => {
    const rawValue = item.value;
    let value: Record<string, unknown> = {};
    if (typeof rawValue === 'string') {
      try {
        const parsed = JSON.parse(rawValue);
        value = typeof parsed === 'object' && parsed !== null ? parsed : { text: rawValue };
      } catch {
        value = { text: rawValue };
      }
    } else if (typeof rawValue === 'object' && rawValue !== null) {
      value = { ...rawValue };
    }

    const sensorId = String(item.sensor_id || '').trim();
    const next = {
      setting_id: String(item.setting_id || createId()),
      client_id: String(item.client_id || client?.client_id || ''),
      project_id: String(item.project_id || project?.project_id || ''),
      sensor_id: sensorId,
      key: String(item.key || ''),
      value: sensorId ? { ...value, sensor_id: sensorId } : value
    };

    await handleSave('projectSettings', 'setting_id')(next, mode);
  };

  const userRoleOptions = isClientMode
    ? [
        { value: CLIENT_ADMIN_ROLE, label: 'client_admin' },
        { value: 'supervisor', label: 'supervisor' },
        { value: 'operator', label: 'operator' },
        { value: 'viewer', label: 'viewer' }
      ]
    : [
        { value: 'system_administrator', label: 'system_administrator' },
        { value: CLIENT_ADMIN_ROLE, label: 'client_admin' },
        { value: 'admin', label: 'admin' },
        { value: 'supervisor', label: 'supervisor' },
        { value: 'operator', label: 'operator' },
        { value: 'viewer', label: 'viewer' }
      ];
  const userHiddenFields = [
    ...(isClientMode
      ? ['user_id', 'auth_user_id', 'client_id', 'client_ids', 'project_scope', 'project_ids', 'username', 'email', 'role', 'is_active']
      : ['client_id'])
  ];
  const userFields = hideFields(makeFieldsReadOnly([
    { key: 'user_id', label: 'User ID', required: true },
    { key: 'auth_user_id', label: 'Auth user ID', readOnly: true, helperText: 'Linked after the user accepts the Supabase Auth invite.' },
    {
      key: 'client_ids',
      label: 'Clientes com acesso',
      required: true,
      type: 'multiselect',
      placeholder: 'Selecionar clientes',
      options: scopedDataset.clients.map((item) => ({ value: item.client_id, label: item.name })),
      onValueChange: (value, draft) => {
        const clientIds = Array.isArray(value) ? value.map(String).filter(Boolean) : [];
        const activeProjectIds = new Set(
          scopedDataset.projects
            .filter((project) => clientIds.includes(project.client_id))
            .map((project) => project.project_id)
        );
        const projectIds = (Array.isArray(draft.project_ids) ? draft.project_ids : [])
          .map(String)
          .filter((projectId) => activeProjectIds.has(projectId));
        return {
          client_ids: clientIds,
          client_id: clientIds[0] ?? null,
          project_ids: projectIds,
          project_id: projectIds[0] ?? null,
          project_scope: projectIds.length ? draft.project_scope ?? 'specific' : 'all'
        };
      },
      helperText: 'Define os clientes onde este perfil pode operar. O primeiro cliente fica como âmbito principal para compatibilidade.'
    },
    {
      key: 'client_id',
      label: 'Cliente principal',
      type: 'select',
      options: scopedDataset.clients.map((item) => ({ value: item.client_id, label: item.name })),
      onValueChange: (value) => ({
        client_ids: value ? [String(value)] : [],
        project_scope: 'all',
        project_ids: [],
        project_id: null
      })
    },
    {
      key: 'project_scope',
      label: 'Âmbito dos projetos',
      required: true,
      type: 'select',
      options: [
        { value: 'all', label: 'Todos os projetos dos clientes selecionados' },
        { value: 'specific', label: 'Projetos específicos' }
      ],
      onValueChange: (value, draft) => {
        if (value === 'all') {
          return { project_ids: [], project_id: null };
        }
        const currentProjectIds = Array.isArray(draft.project_ids) ? draft.project_ids.map(String).filter(Boolean) : [];
        const fallbackProjectId = String(draft.project_id ?? '').trim();
        const nextProjectIds = currentProjectIds.length ? currentProjectIds : fallbackProjectId ? [fallbackProjectId] : [];
        return { project_ids: nextProjectIds, project_id: nextProjectIds[0] ?? null };
      }
    },
    {
      key: 'project_ids',
      label: 'Projetos',
      type: 'multiselect',
      placeholder: 'Selecionar projetos',
      visibleWhen: (draft) => draft.project_scope === 'specific',
      getOptions: (draft) => {
        const clientIds = Array.isArray(draft.client_ids) && draft.client_ids.length
          ? draft.client_ids.map(String).filter(Boolean)
          : [draft.client_id].filter(Boolean).map(String);
        return scopedDataset.projects
          .filter((project) => clientIds.includes(project.client_id))
          .map((item) => {
            const projectClient = scopedDataset.clients.find((client) => client.client_id === item.client_id);
            return { value: item.project_id, label: projectClient ? `${projectClient.name} / ${item.name}` : item.name };
          });
      },
      onValueChange: (value) => {
        const projectIds = Array.isArray(value) ? value.map(String).filter(Boolean) : [];
        return { project_ids: projectIds, project_id: projectIds[0] ?? null };
      },
      helperText: 'Quando escolhes projetos específicos, a lista inclui os projetos de todos os clientes selecionados. Para acesso global, usa "Todos os projetos dos clientes selecionados".'
    },
    { key: 'name', label: 'Nome', required: true },
    { key: 'job_title', label: 'Função na empresa', placeholder: 'Ex: Biólogo, parasitologista, cientista de dados, técnico de produção' },
    { key: 'username', label: 'Username', required: true },
    { key: 'email', label: 'Email', required: true, type: 'email' },
    { key: 'mobile_phone', label: 'Telemóvel para SMS', type: 'tel', placeholder: '+351 912 345 678', helperText: 'Formato internacional para alertas SMS via Brevo, por exemplo +351912345678.' },
    { key: 'alert_notifications_enabled', label: 'Receber alertas', type: 'toggle', helperText: 'Desativa notificações automáticas de alerta para este utilizador.' },
    { key: 'role', label: 'Role', required: true, type: 'select', options: userRoleOptions },
    { key: 'is_active', label: 'Status', type: 'status' }
  ], ['user_id', ...(isClientMode ? ['client_id', 'username', 'email', 'role', 'is_active'] : [])]), userHiddenFields);

  const cameraItems = scopedDataset.cameras.map(enrichAssignableScope);
  const ultrasoundItems = scopedDataset.ultrasoundDevices.map(enrichAssignableScope);
  const sensorItems = scopedDataset.sensors.map(enrichAssignableScope);
  const userItems = scopedDataset.users.map((item) => {
    const clientIds = Array.from(new Set([
      ...((item.client_ids ?? []).map(String).filter(Boolean)),
      ...(item.client_id ? [item.client_id] : [])
    ]));
    const projectIds = Array.from(new Set([
      ...((item.project_ids ?? []).map(String).filter(Boolean)),
      ...(item.project_id ? [item.project_id] : [])
    ]));
    return {
      ...item,
      client_ids: clientIds,
      client_id: item.client_id ?? clientIds[0] ?? null,
      project_ids: projectIds,
      project_id: projectIds[0] ?? null,
      project_scope: projectIds.length ? 'specific' : 'all'
    };
  });

  const architectureSections = useMemo(
    () => OPERATIONAL_ARCHITECTURE_SECTION_IDS
      .map((sectionId) => availableSections.find((item) => item.id === sectionId))
      .filter(Boolean) as ConfigurationSectionDefinition[],
    [availableSections]
  );
  const architectureSectionById = useMemo(
    () => new Map(architectureSections.map((item) => [item.id, item])),
    [architectureSections]
  );
  const getArchitectureSection = (sectionId: SectionId) =>
    architectureSectionById.get(sectionId) ?? availableSections.find((item) => item.id === sectionId) ?? section;

  const createArchitectureDraft = (sectionId: SectionId, value: Record<string, any>) => {
    const draftType = String(value.type ?? '');
    switch (sectionId) {
      case 'client':
        return {
          client_id: createId(),
          name: '',
          slug: '',
          is_active: true
        };
      case 'projects':
        return {
          project_id: createId(),
          client_id: client?.client_id ?? '',
          name: '',
          slug: '',
          context_type: 'aquaculture',
          species_focus: '',
          default_phase: '',
          is_active: true
        };
      case 'locations':
        return {
          location_id: createId(),
          client_id: value.client_id ?? client?.client_id ?? '',
          project_id: value.project_id ?? project?.project_id ?? '',
          name: '',
          type: getDefaultOperationalLocationType(project?.context_type),
          address: '',
          city: '',
          postal_code: '',
          country: '',
          latitude: null,
          longitude: null,
          is_active: true
        };
      case 'zones':
        return {
          zone_id: createId(),
          location_id: value.location_id ?? location?.location_id ?? '',
          client_id: value.client_id ?? client?.client_id ?? '',
          project_id: value.project_id ?? project?.project_id ?? '',
          name: '',
          code: '',
          type: getOperationalContextProfile(project?.context_type).areaTypeOptions[0]?.value ?? 'operational_area',
          species: project?.species_focus ?? '',
          phase: project?.default_phase ?? '',
          is_active: true
        };
      case 'kits':
        return {
          kit_id: createId(),
          client_id: value.client_id ?? (isClientMode ? client?.client_id ?? '' : ''),
          location_id: value.location_id ?? (isClientMode ? location?.location_id ?? '' : ''),
          project_id: value.project_id ?? (isClientMode ? project?.project_id ?? '' : ''),
          zone_id: value.zone_id ?? (isClientMode ? zone?.zone_id ?? '' : ''),
          name: '',
          kit_type: draftType === 'kit' ? 'vision' : 'vision',
          controller_profile: 'atronia-atlas',
          connectivity_mode: 'ethernet',
          offline_buffer_hours: 72,
          bulk_batch_size: 500,
          local_alerting_enabled: true,
          configuration_revision: 1,
          status: 'active',
          description: '',
          capture_interval_seconds: 60,
          frame_interval_ms: 1000,
          storage_bucket: 'captures',
          jpg_quality: 70,
          live_frame_path_template: 'live/{camera_code}/feed.jpg',
          is_active: true
        };
      case 'cameras':
        return {
          camera_id: createId(),
          client_id: value.client_id ?? (isClientMode ? client?.client_id ?? '' : ''),
          project_id: value.project_id ?? (isClientMode ? project?.project_id ?? '' : ''),
          location_id: value.location_id ?? (isClientMode ? location?.location_id ?? '' : ''),
          kit_id: value.kit_id ?? (isClientMode ? scopedDataset.kits[0]?.kit_id ?? '' : ''),
          zone_id: value.zone_id ?? (isClientMode ? zone?.zone_id ?? '' : ''),
          name: '',
          camera_code: '',
          source_type: 'storage',
          source_uri: '',
          live_feed_path: '',
          capture_interval_seconds: 30,
          frame_interval_ms: 1000,
          training_burst_available: false,
          training_burst_enabled: false,
          training_burst_target: 50,
          resolution: '',
          position_description: '',
          is_primary: false,
          is_active: true
        };
      case 'sensors':
        return {
          sensor_id: createId(),
          client_id: value.client_id ?? (isClientMode ? client?.client_id ?? '' : ''),
          project_id: value.project_id ?? (isClientMode ? project?.project_id ?? '' : ''),
          location_id: value.location_id ?? (isClientMode ? location?.location_id ?? '' : ''),
          kit_id: value.kit_id ?? (isClientMode ? scopedDataset.kits[0]?.kit_id ?? '' : ''),
          zone_id: value.zone_id ?? (isClientMode ? zone?.zone_id ?? '' : ''),
          sensor_type: '',
          name: '',
          unit: '',
          min_threshold: null,
          max_threshold: null,
          recommended_min: null,
          recommended_max: null,
          last_value: null,
          is_active: true
        };
      case 'ultrasound':
        return {
          device_id: createId(),
          client_id: value.client_id ?? (isClientMode ? client?.client_id ?? '' : ''),
          project_id: value.project_id ?? (isClientMode ? project?.project_id ?? '' : ''),
          location_id: value.location_id ?? (isClientMode ? location?.location_id ?? '' : ''),
          zone_id: value.zone_id ?? (isClientMode ? zone?.zone_id ?? '' : ''),
          kit_id: value.kit_id ?? (isClientMode ? scopedDataset.kits[0]?.kit_id ?? '' : ''),
          name: '',
          device_type: 'ultrasound',
          status: 'active',
          description: '',
          is_active: true
        };
      case 'thresholds':
        return {
          setting_id: createId(),
          client_id: value.client_id ?? client?.client_id ?? '',
          project_id: value.project_id ?? project?.project_id ?? '',
          sensor_id: value.sensor_id ?? getRuleSensorId(value),
          key: '',
          value: {}
        };
      default:
        return value;
    }
  };

  const openArchitectureNode = (sectionId: SectionId, value: Record<string, any>, meta?: { draftNodeId?: string }) => {
    if (!ARCHITECTURE_EDITABLE_SECTION_IDS.includes(sectionId)) return;
    setArchitectureSelection({
      sectionId,
      value: meta?.draftNodeId ? createArchitectureDraft(sectionId, value) : value,
      mode: meta?.draftNodeId ? 'create' : 'edit',
      draftNodeId: meta?.draftNodeId
    });
  };
  const openArchitectureSection = (sectionId: SectionId) => {
    setActiveSection(sectionId);
    setActiveView(sectionId);
    setNavOpen(false);
  };

  const architectureEditor = architectureSelection ? (() => {
    const selectedSection = getArchitectureSection(architectureSelection.sectionId);
    switch (architectureSelection.sectionId) {
      case 'client':
        return {
          section: selectedSection,
          fields: clientFields,
          value: architectureSelection.value,
          onSave: handleSave('clients', 'client_id'),
          onDelete: handleDelete('clients', 'client_id')
        };
      case 'projects':
        return {
          section: selectedSection,
          fields: projectFields,
          value: architectureSelection.value,
          onSave: handleSave('projects', 'project_id'),
          onDelete: handleDelete('projects', 'project_id')
        };
      case 'locations':
        return {
          section: selectedSection,
          fields: locationFields,
          value: architectureSelection.value,
          onSave: handleSave('locations', 'location_id'),
          onDelete: handleDelete('locations', 'location_id')
        };
      case 'zones':
        return {
          section: selectedSection,
          fields: zoneFields,
          value: architectureSelection.value,
          onSave: (item: Record<string, any>, mode: 'create' | 'edit') =>
            handleSave('zones', 'zone_id')(normalizeZoneScope(item) as AqlConfigurationDataset['zones'][number], mode),
          onDelete: handleDelete('zones', 'zone_id')
        };
      case 'kits':
        return {
          section: selectedSection,
          fields: kitFields,
          value: architectureSelection.value,
          onSave: (item: Record<string, any>, mode: 'create' | 'edit') =>
            handleSave('kits', 'kit_id')(normalizeKitScope(item) as AqlConfigurationDataset['kits'][number], mode),
          onDelete: handleDelete('kits', 'kit_id')
        };
      case 'cameras':
        return {
          section: selectedSection,
          fields: cameraFields,
          value: architectureSelection.value,
          onSave: (item: AqlCamera, mode: 'create' | 'edit') =>
            handleSave('cameras', 'camera_id')(normalizeCameraForSave(item), mode),
          onDelete: handleDelete('cameras', 'camera_id')
        };
      case 'sensors':
        return {
          section: selectedSection,
          fields: sensorFields,
          value: architectureSelection.value,
          onSave: (item: Record<string, any>, mode: 'create' | 'edit') =>
            handleSave('sensors', 'sensor_id')(normalizeSensorForSave(item), mode),
          onDelete: handleDelete('sensors', 'sensor_id')
        };
      case 'ultrasound':
        return {
          section: selectedSection,
          fields: ultrasoundFields,
          value: architectureSelection.value,
          onSave: (item: Record<string, any>, mode: 'create' | 'edit') =>
            handleSave('ultrasoundDevices', 'device_id')(normalizeUltrasoundForSave(item), mode),
          onDelete: handleDelete('ultrasoundDevices', 'device_id')
        };
      case 'thresholds':
        return {
          section: selectedSection,
          fields: thresholdFields,
          value: {
            ...architectureSelection.value,
            sensor_id: getRuleSensorId(architectureSelection.value)
          },
          onSave: saveThresholdSetting,
          onDelete: handleDelete('projectSettings', 'setting_id')
        };
      default:
        return null;
    }
  })() : null;

  const operationalRuleItems = scopedDataset.projectSettings.filter((item) => item.key !== VISION_COPILOT_SETTING_KEY);
  const fallbackArchitectureClient: AqlClient | null = isClientMode && !client
    ? {
        client_id: scopedDataset.projects[0]?.client_id || portalAccess?.clientId || selectedClient?.id || resolvedClientSlug || 'client',
        name: selectedClient?.name || resolvedClientSlug || 'Cliente',
        slug: selectedClient?.slug || resolvedClientSlug || '',
        is_active: true
      }
    : null;
  const architectureClient = client ?? fallbackArchitectureClient;
  const architectureProjects = useMemo(() => {
    if (!architectureClient) return scopedDataset.projects;

    const matchedProjects = scopedDataset.projects.filter(
      (item) => item.client_id === architectureClient.client_id
    );
    return matchedProjects.length ? matchedProjects : scopedDataset.projects;
  }, [architectureClient, scopedDataset.projects]);
  const architectureClients = useMemo(
    () => isClientMode
      ? architectureClient ? [architectureClient] : []
      : scopedDataset.clients,
    [architectureClient, isClientMode, scopedDataset.clients]
  );
  const architectureFlowProjects = useMemo(
    () => isClientMode ? architectureProjects : scopedDataset.projects,
    [architectureProjects, isClientMode, scopedDataset.projects]
  );
  const architectureMetrics = {
    projects: architectureFlowProjects.length,
    locations: scopedDataset.locations.length,
    zones: scopedDataset.zones.length,
    kits: scopedDataset.kits.length,
    cameras: scopedDataset.cameras.length,
    sensors: scopedDataset.sensors.length,
    ultrasound: scopedDataset.ultrasoundDevices.length,
    rules: operationalRuleItems.length
  };
  const renderArchitectureNode = ({
    id,
    tone,
    icon: Icon,
    eyebrow,
    title,
    subtitle,
    meta,
    status,
    onClick
  }: {
    id: string;
    tone: 'client' | 'project' | 'location' | 'zone' | 'kit' | 'camera' | 'sensor' | 'ultrasound' | 'threshold';
    icon: ComponentType<{ className?: string }>;
    eyebrow: string;
    title: string;
    subtitle?: string;
    meta?: string;
    status?: string;
    onClick: () => void;
  }) => (
    <button
      key={id}
      type="button"
      onClick={onClick}
      className={`group relative min-h-[68px] w-full max-w-[300px] rounded-xl border p-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/35 ${architectureNodeToneClass(tone)}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-current/15 bg-white/55 text-current shadow-sm dark:bg-white/5">
            <Icon className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className="block text-[9px] font-bold uppercase tracking-[0.16em] text-current/55">{eyebrow}</span>
            <span className="mt-0.5 block truncate text-sm font-bold text-current">{title}</span>
            {subtitle ? <span className="mt-0.5 block truncate text-xs font-medium text-current/62">{subtitle}</span> : null}
          </span>
        </div>
        {status ? (
          <span className="shrink-0 rounded-full border border-current/15 bg-white/45 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-current/70 dark:bg-white/5">
            {status}
          </span>
        ) : null}
      </div>
      {meta ? (
        <div className="mt-2 border-t border-current/10 pt-2 text-[11px] font-semibold leading-4 text-current/62">
          {meta}
        </div>
      ) : null}
    </button>
  );
  const renderLegacyArchitectureCanvas = () => (
    <div className="min-h-[calc(100dvh-7rem)] overflow-auto bg-[radial-gradient(circle_at_1px_1px,rgba(14,165,233,0.18)_1px,transparent_0)] p-4 [background-size:22px_22px] sm:p-6">
      <div className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-primary">Configuração visual</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-foreground">{client?.name ?? 'Arquitetura operacional'}</h1>
        <p className="mt-1 text-sm font-medium text-muted-foreground">Cards ligados por relações operacionais. Clica num card para editar.</p>
      </div>

      {client ? (
        <div className="space-y-8">
          {renderArchitectureNode({
            id: client.client_id,
            tone: 'client',
            icon: BuildingOfficeIcon,
            eyebrow: 'Cliente',
            title: client.name,
            subtitle: client.slug ? `/${client.slug}` : 'Código por definir',
            meta: client.is_active ? 'Ativo' : 'Inativo',
            onClick: () => openArchitectureNode('client', client)
          })}

          <div className="relative ml-6 space-y-10 border-l-2 border-dashed border-sky-300/70 pl-8">
            {architectureProjects.length ? architectureProjects.map((projectItem) => {
              const projectLocations = scopedDataset.locations.filter((locationItem) =>
                locationItem.project_id === projectItem.project_id
              );
              const projectLocationIds = new Set(projectLocations.map((item) => item.location_id));
              const projectZones = scopedDataset.zones.filter((zoneItem) =>
                zoneItem.project_id === projectItem.project_id ||
                projectLocationIds.has(zoneItem.location_id)
              );
              const projectZoneIds = new Set(projectZones.map((item) => item.zone_id));
              const projectKits = scopedDataset.kits.filter((kitItem) =>
                kitItem.project_id === projectItem.project_id ||
                projectLocationIds.has(kitItem.location_id) ||
                (kitItem.zone_id ? projectZoneIds.has(kitItem.zone_id) : false)
              );
              const projectKitIds = new Set(projectKits.map((item) => item.kit_id));
              const projectCameras = scopedDataset.cameras.filter((cameraItem) =>
                (cameraItem.kit_id ? projectKitIds.has(cameraItem.kit_id) : false) ||
                (cameraItem.zone_id ? projectZoneIds.has(cameraItem.zone_id) : false)
              );
              const projectSensors = scopedDataset.sensors.filter((sensorItem) =>
                projectKitIds.has(sensorItem.kit_id) ||
                (sensorItem.zone_id ? projectZoneIds.has(sensorItem.zone_id) : false)
              );
              const projectUltrasound = scopedDataset.ultrasoundDevices.filter((deviceItem) => projectKitIds.has(deviceItem.kit_id));
              const projectRules = operationalRuleItems.filter((ruleItem) =>
                ruleItem.project_id === projectItem.project_id || ruleItem.client_id === projectItem.client_id
              );

              return (
                <div key={projectItem.project_id} className="relative before:absolute before:-left-8 before:top-8 before:w-8 before:border-t-2 before:border-dashed before:border-sky-300/70">
                  {renderArchitectureNode({
                    id: projectItem.project_id,
                    tone: 'project',
                    icon: Bars3BottomLeftIcon,
                    eyebrow: 'Projeto',
                    title: projectItem.name,
                    subtitle: [projectItem.species_focus, projectItem.default_phase].filter(Boolean).join(' · ') || getOperationalContextProfile(projectItem.context_type).categoryPhaseFallback,
                    meta: `${formatCount(projectLocations.length, 'instalação', 'instalações')} · ${formatCount(projectZones.length, 'área', 'áreas')} · ${formatCount(projectKits.length, 'unidade', 'unidades')}`,
                    status: projectItem.is_active ? 'ativo' : 'inativo',
                    onClick: () => openArchitectureNode('projects', projectItem)
                  })}

                  <div className="relative ml-6 mt-5 space-y-8 border-l-2 border-dashed border-emerald-300/70 pl-8">
                    {projectLocations.length ? projectLocations.map((locationItem) => {
                      const locationZones = projectZones.filter((zoneItem) => zoneItem.location_id === locationItem.location_id);
                      const locationZoneIds = new Set(locationZones.map((item) => item.zone_id));
                      const locationKits = projectKits.filter((kitItem) =>
                        kitItem.location_id === locationItem.location_id ||
                        (kitItem.zone_id ? locationZoneIds.has(kitItem.zone_id) : false)
                      );

                      return (
                        <div key={locationItem.location_id} className="relative before:absolute before:-left-8 before:top-8 before:w-8 before:border-t-2 before:border-dashed before:border-emerald-300/70">
                          {renderArchitectureNode({
                            id: locationItem.location_id,
                            tone: 'location',
                            icon: MapPinIcon,
                            eyebrow: 'Instalação',
                            title: locationItem.name,
                            subtitle: [locationItem.city, locationItem.country].filter(Boolean).join(', ') || locationItem.type || 'Localização por definir',
                            meta: locationItem.latitude != null && locationItem.longitude != null ? 'Georreferenciada' : 'Sem coordenadas',
                            status: locationItem.is_active ? 'ativa' : 'inativa',
                            onClick: () => openArchitectureNode('locations', locationItem)
                          })}

                          <div className="relative ml-6 mt-5 space-y-8 border-l-2 border-dashed border-indigo-300/70 pl-8">
                            {locationZones.length ? locationZones.map((zoneItem) => {
                              const zoneKits = locationKits.filter((kitItem) => kitItem.zone_id === zoneItem.zone_id);
                              return (
                                <div key={zoneItem.zone_id} className="relative before:absolute before:-left-8 before:top-8 before:w-8 before:border-t-2 before:border-dashed before:border-indigo-300/70">
                                  {renderArchitectureNode({
                                    id: zoneItem.zone_id,
                                    tone: 'zone',
                                    icon: ArrowTrendingUpIcon,
                                    eyebrow: getOperationalContextProfile(projectItem.context_type).areaLabel,
                                    title: zoneItem.name,
                                    subtitle: [zoneItem.species, zoneItem.phase].filter(Boolean).join(' · ') || zoneItem.type || 'Área por caracterizar',
                                    meta: zoneItem.code ? `Código ${zoneItem.code}` : 'Sem código operacional',
                                    status: zoneItem.is_active ? 'ativa' : 'inativa',
                                    onClick: () => openArchitectureNode('zones', zoneItem)
                                  })}

                                  <div className="relative ml-6 mt-5 flex flex-wrap gap-x-14 gap-y-8 border-l-2 border-dashed border-orange-300/70 pl-8">
                                    {zoneKits.length ? zoneKits.map((kitItem) => {
                                      const kitCameras = projectCameras.filter((cameraItem) => cameraItem.kit_id === kitItem.kit_id);
                                      const kitSensors = projectSensors.filter((sensorItem) => sensorItem.kit_id === kitItem.kit_id);
                                      const kitUltrasound = projectUltrasound.filter((deviceItem) => deviceItem.kit_id === kitItem.kit_id);
                                      const kitHasIssues = kitItem.status !== 'active' || !kitCameras.length && !kitSensors.length && !kitUltrasound.length;

                                      return (
                                        <div key={kitItem.kit_id} className="relative min-w-[300px] before:absolute before:-left-8 before:top-8 before:w-8 before:border-t-2 before:border-dashed before:border-orange-300/70">
                                          {renderArchitectureNode({
                                            id: kitItem.kit_id,
                                            tone: 'kit',
                                            icon: CpuChipIcon,
                                            eyebrow: 'Unidade edge',
                                            title: kitItem.name,
                                            subtitle: kitItem.kit_type || 'Tipo por definir',
                                            meta: `${formatCount(kitCameras.length, 'câmara', 'câmaras')} · ${formatCount(kitSensors.length, 'sensor', 'sensores')} · ${formatCount(kitUltrasound.length, 'sonda', 'sondas')}`,
                                            status: kitHasIssues ? 'rever' : kitItem.status,
                                            onClick: () => openArchitectureNode('kits', kitItem)
                                          })}

                                          <div className="relative ml-6 mt-4 space-y-3 border-l-2 border-dashed border-slate-300/80 pl-8">
                                            {kitCameras.map((cameraItem) => (
                                              <div key={cameraItem.camera_id} className="relative before:absolute before:-left-8 before:top-8 before:w-8 before:border-t-2 before:border-dashed before:border-violet-300/70">
                                                {renderArchitectureNode({
                                                  id: cameraItem.camera_id,
                                                  tone: 'camera',
                                                  icon: CameraIcon,
                                                  eyebrow: 'Câmara',
                                                  title: cameraItem.name || cameraItem.camera_code || 'Câmara sem nome',
                                                  subtitle: cameraItem.live_feed_path || cameraItem.source_type || 'Feed por definir',
                                                  meta: cameraItem.last_frame_status || 'Sem frame recente',
                                                  status: cameraItem.is_active ? 'ativa' : 'inativa',
                                                  onClick: () => openArchitectureNode('cameras', cameraItem)
                                                })}
                                              </div>
                                            ))}
                                            {kitSensors.map((sensorItem) => {
                                              const normalizedType = String(sensorItem.sensor_type ?? '').toLowerCase();
                                              const SensorIcon = sensorIconMap[normalizedType] ?? Cog6ToothIcon;
                                              const label = sensorLabelMap[normalizedType] ?? sensorItem.name ?? 'Sensor';
                                              const value = typeof sensorItem.last_value === 'number'
                                                ? `${sensorItem.last_value}${sensorItem.unit ? ` ${sensorItem.unit}` : ''}`
                                                : 'Sem leitura';
                                              return (
                                                <div key={sensorItem.sensor_id} className="relative before:absolute before:-left-8 before:top-8 before:w-8 before:border-t-2 before:border-dashed before:border-blue-300/70">
                                                  {renderArchitectureNode({
                                                    id: sensorItem.sensor_id,
                                                    tone: 'sensor',
                                                    icon: SensorIcon,
                                                    eyebrow: 'Sensor',
                                                    title: sensorItem.name || label,
                                                    subtitle: label,
                                                    meta: value,
                                                    status: sensorItem.is_active ? 'ativo' : 'inativo',
                                                    onClick: () => openArchitectureNode('sensors', sensorItem)
                                                  })}
                                                </div>
                                              );
                                            })}
                                            {kitUltrasound.map((deviceItem) => (
                                              <div key={deviceItem.device_id} className="relative before:absolute before:-left-8 before:top-8 before:w-8 before:border-t-2 before:border-dashed before:border-cyan-300/70">
                                                {renderArchitectureNode({
                                                  id: deviceItem.device_id,
                                                  tone: 'ultrasound',
                                                  icon: RectangleStackIcon,
                                                  eyebrow: 'Sonda',
                                                  title: deviceItem.name,
                                                  subtitle: deviceItem.description || 'Sonda associada à unidade',
                                                  meta: deviceItem.status,
                                                  status: deviceItem.is_active ? 'ativa' : 'inativa',
                                                  onClick: () => openArchitectureNode('ultrasound', deviceItem)
                                                })}
                                              </div>
                                            ))}
                                            {!kitCameras.length && !kitSensors.length && !kitUltrasound.length ? (
                                              <button
                                                type="button"
                                                onClick={() => openArchitectureSection('cameras')}
                                                className="relative w-full max-w-[300px] rounded-xl border border-dashed border-border bg-muted/20 px-3 py-3 text-left text-xs font-semibold text-muted-foreground transition-colors before:absolute before:-left-8 before:top-1/2 before:w-8 before:border-t-2 before:border-dashed before:border-slate-300/80 hover:bg-muted hover:text-foreground"
                                              >
                                                Unidade sem câmaras, sensores ou sondas configurados
                                              </button>
                                            ) : null}
                                          </div>
                                        </div>
                                      );
                                    }) : (
                                      <button
                                        type="button"
                                        onClick={() => openArchitectureSection('kits')}
                                        className="relative w-full max-w-[300px] rounded-xl border border-dashed border-border bg-muted/20 px-3 py-3 text-left text-xs font-semibold text-muted-foreground transition-colors before:absolute before:-left-8 before:top-1/2 before:w-8 before:border-t-2 before:border-dashed before:border-orange-300/70 hover:bg-muted hover:text-foreground"
                                      >
                                        Área sem unidade edge associada
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            }) : (
                              <button
                                type="button"
                                onClick={() => openArchitectureSection('zones')}
                                className="relative w-full max-w-[300px] rounded-xl border border-dashed border-border bg-muted/20 px-3 py-3 text-left text-xs font-semibold text-muted-foreground transition-colors before:absolute before:-left-8 before:top-1/2 before:w-8 before:border-t-2 before:border-dashed before:border-indigo-300/70 hover:bg-muted hover:text-foreground"
                              >
                                Instalação sem áreas operacionais
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    }) : (
                      <button
                        type="button"
                        onClick={() => openArchitectureSection('locations')}
                        className="relative w-full max-w-[300px] rounded-xl border border-dashed border-border bg-muted/20 px-3 py-3 text-left text-xs font-semibold text-muted-foreground transition-colors before:absolute before:-left-8 before:top-1/2 before:w-8 before:border-t-2 before:border-dashed before:border-emerald-300/70 hover:bg-muted hover:text-foreground"
                      >
                        Projeto sem instalações configuradas
                      </button>
                    )}

                    {projectRules.length ? (
                      <div className="relative ml-6 mt-5 flex flex-wrap gap-4 border-l-2 border-dashed border-rose-300/70 pl-8">
                        {projectRules.map((ruleItem) => (
                          <div key={ruleItem.setting_id} className="relative before:absolute before:-left-8 before:top-8 before:w-8 before:border-t-2 before:border-dashed before:border-rose-300/70">
                            {renderArchitectureNode({
                              id: ruleItem.setting_id,
                              tone: 'threshold',
                              icon: ShieldCheckIcon,
                              eyebrow: 'Regra de alertas',
                              title: ruleItem.key || 'Regra sem chave',
                              subtitle: scopedDataset.projects.find((item) => item.project_id === ruleItem.project_id)?.name ?? 'Âmbito por confirmar',
                              meta: 'Aplicada à operação',
                              onClick: () => openArchitectureNode('thresholds', ruleItem)
                            })}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            }) : (
              <button
                type="button"
                onClick={() => openArchitectureSection('projects')}
                className="relative w-full max-w-[300px] rounded-xl border border-dashed border-border bg-muted/20 px-3 py-3 text-left text-xs font-semibold text-muted-foreground transition-colors before:absolute before:-left-8 before:top-1/2 before:w-8 before:border-t-2 before:border-dashed before:border-sky-300/70 hover:bg-muted hover:text-foreground"
              >
                Cliente sem projetos configurados
              </button>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => openArchitectureSection('client')}
          className="w-full max-w-[300px] rounded-xl border border-dashed border-border bg-muted/20 px-3 py-3 text-left text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          Começa por configurar o cliente para montar a arquitetura operacional.
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <AqlVisionLoader
            compact
            label="A carregar configuração"
            detail="A preparar cliente, áreas monitorizadas, unidades edge e sensores."
            className="items-start text-left"
          />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="m-3 flex min-h-[320px] items-center justify-center rounded-2xl border border-amber-400/30 bg-card p-6 shadow-sm sm:m-4">
        <div className="max-w-xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-500">Dados operacionais indisponíveis</p>
          <h2 className="mt-2 text-xl font-semibold text-foreground">Não foi possível carregar a gestão de projetos</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            A sessão pode ter expirado ou a ligação aos dados foi interrompida. Nenhuma configuração foi alterada.
          </p>
          <button
            type="button"
            onClick={() => setLoadRevision((current) => current + 1)}
            className="mt-5 inline-flex h-10 items-center justify-center rounded-xl bg-primary px-4 text-sm font-bold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (isClientMode && resolvedClientSlug && !client) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 shadow-sm">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Client Portal</p>
        <h1 className="mt-2 text-2xl font-semibold text-foreground">Client not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          No client with the access code <span className="font-medium text-foreground">{resolvedClientSlug}</span> exists in the current configuration dataset.
        </p>
      </div>
    );
  }

  return (
    <div className="aql-density-text flex h-full min-h-full flex-1 flex-col self-stretch animate-in fade-in duration-500">
      <style>
        {`
          @keyframes aqlConfigFloatIn {
            0% {
              opacity: 0;
              transform: translate3d(0, 18px, 0) scale(0.985);
              filter: blur(12px);
            }
            100% {
              opacity: 1;
              transform: translate3d(0, 0, 0) scale(1);
              filter: blur(0);
            }
          }

          @keyframes aqlConfigHalo {
            0%, 100% { opacity: 0.28; transform: scaleX(0.92); }
            50% { opacity: 0.7; transform: scaleX(1); }
          }

          @keyframes aqlConfigNavActive {
            0%, 100% { transform: translateX(0); box-shadow: 0 0 0 rgba(56, 189, 248, 0); }
            50% { transform: translateX(2px); box-shadow: 0 14px 30px -24px rgba(56, 189, 248, 0.65); }
          }

          @keyframes aqlConfigNavIcon {
            0%, 100% { transform: translateY(0) scale(1); }
            50% { transform: translateY(-1px) scale(1.04); }
          }

          .aql-config-float-in {
            animation: aqlConfigFloatIn 720ms cubic-bezier(0.22, 1, 0.36, 1) both;
          }

          .aql-config-halo::after {
            content: "";
            position: absolute;
            inset: auto 22px -1px;
            height: 1px;
            background: linear-gradient(90deg, transparent, rgba(2, 43, 137, 0.55), rgba(3, 61, 156, 0.18), transparent);
            animation: aqlConfigHalo 5.8s ease-in-out infinite;
            pointer-events: none;
          }

          .aql-config-nav-button {
            transform-origin: left center;
          }

          .aql-config-nav-button:hover {
            transform: none;
          }

          .aql-config-nav-button:hover .aql-config-nav-icon {
            transform: none;
          }

          .aql-config-nav-button[data-active="true"] {
            animation: none;
          }

          .aql-config-nav-button[data-active="true"] .aql-config-nav-icon {
            animation: none;
          }

          @media (prefers-reduced-motion: reduce) {
            .aql-config-float-in,
            .aql-config-halo::after,
            .aql-config-nav-button[data-active="true"],
            .aql-config-nav-button[data-active="true"] .aql-config-nav-icon {
              animation: none !important;
            }

            .aql-config-nav-button,
            .aql-config-nav-button:hover,
            .aql-config-nav-button:hover .aql-config-nav-icon {
              transform: none !important;
            }
          }
        `}
      </style>

      <section
        className={`aql-adaptive-panels relative grid min-h-[calc(100dvh-3.5rem)] flex-1 overflow-hidden ${
          navCollapsed ? 'xl:grid-cols-[4.5rem_minmax(0,1fr)]' : 'xl:grid-cols-[18rem_minmax(0,1fr)]'
        }`}
      >
        {navOpen ? (
          <button
            type="button"
            className="fixed inset-0 z-40 bg-slate-950/36 backdrop-blur-sm transition-opacity xl:hidden"
            onClick={() => setNavOpen(false)}
            aria-label="Fechar menu de configuração"
          />
        ) : null}
        <aside
          className={`aql-config-finder-sidebar aql-config-float-in fixed bottom-0 left-0 top-14 z-50 h-auto w-[min(82vw,310px)] min-h-0 self-stretch overflow-y-auto border-r border-border bg-card p-0 shadow-2xl transition-[width,transform] duration-300 ease-out xl:relative xl:inset-auto xl:z-auto xl:h-auto xl:min-h-[calc(100dvh-3.5rem)] xl:translate-x-0 xl:bg-muted/20 xl:shadow-none ${
            navCollapsed ? 'xl:w-[4.5rem]' : 'xl:w-72'
          } ${
            navOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{ animationDelay: '120ms' }}
        >
          <div className="flex h-14 items-center justify-between border-b border-border px-3 xl:hidden">
            <span className="text-sm font-bold text-foreground">Configuração</span>
            <button
              type="button"
              onClick={() => setNavOpen(false)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Fechar menu de configuração"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          <div className={`hidden h-16 items-center border-b border-border px-3 xl:flex ${navCollapsed ? 'justify-center' : 'justify-between'}`}>
            {!navCollapsed ? (
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-foreground">Configuração</p>
                <p className="mt-0.5 truncate text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  Estrutura operacional
                </p>
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => setNavCollapsed((value) => !value)}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/45"
              aria-label={navCollapsed ? 'Expandir menu de configuração' : 'Recolher menu de configuração'}
              title={navCollapsed ? 'Expandir menu' : 'Recolher menu'}
            >
              {navCollapsed ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronLeftIcon className="h-4 w-4" />}
            </button>
          </div>
          <div className="space-y-1 py-2">
            {[
              {
                id: 'operational-architecture' as const,
                label: 'Arquitetura Operacional',
                icon: RectangleStackIcon
              },
              ...standaloneSections.map((item) => ({
                id: item.id,
                label: item.label,
                icon: iconMap[item.id]
              }))
            ].map((item, index) => {
              const active = item.id === activeView;
              const NavIcon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    if (item.id === 'operational-architecture') {
                      setActiveView('operational-architecture');
                    } else {
                      setActiveSection(item.id);
                      setActiveView(item.id);
                    }
                    setNavOpen(false);
                  }}
                  data-active={active}
                  title={navCollapsed ? item.label : undefined}
                  className={`aql-config-nav-button group flex w-full gap-3 border-y border-transparent py-2.5 text-left transition-colors ${
                    navCollapsed ? 'px-5 xl:justify-center xl:gap-0 xl:px-0' : 'px-5'
                  } ${
                    active
                      ? 'border-sky-200/70 bg-sky-500 text-white dark:border-slate-700/55 dark:bg-slate-800/72 dark:text-slate-50'
                      : 'bg-transparent text-slate-600 hover:bg-white/70 hover:text-slate-950 dark:text-slate-300/78 dark:hover:bg-slate-800/45 dark:hover:text-slate-50'
                  }`}
                  style={{ animationDelay: `${180 + index * 45}ms` } as CSSProperties}
                >
                  <div className={`aql-config-nav-icon rounded-lg transition-all duration-300 ${
                    active
                      ? 'text-white dark:text-sky-400'
                      : 'text-slate-500 group-hover:text-sky-600 dark:text-sky-400'
                  }`}>
                    <NavIcon className="h-4 w-4" />
                  </div>
                  <div className={`aql-config-nav-label ${navCollapsed ? 'xl:hidden' : ''}`}>
                    <p className="font-medium">{item.label}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <div className="aql-adaptive-section-height aql-config-float-in min-w-0 overflow-y-auto px-3 py-3 sm:px-4 lg:px-5 lg:py-4" style={{ animationDelay: '200ms' }}>
          <div className="mb-3 flex items-center gap-2 xl:hidden">
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-muted/45 px-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
              aria-label="Abrir menu de configuração"
            >
              <Bars3Icon className="h-4 w-4 text-primary" />
              Secções
            </button>
            <span className="min-w-0 truncate text-sm font-semibold text-muted-foreground">
              {activeView === 'operational-architecture' ? 'Arquitetura Operacional' : section.label}
            </span>
          </div>
          {activeView === 'operational-architecture' ? (
            <OperationalArchitectureFlow
              client={client}
              clients={architectureClients}
              allowClientCreation={!isClientMode}
              discardDraftNodeId={discardDraftNodeId}
              dataset={scopedDataset}
              projects={architectureFlowProjects}
              rules={operationalRuleItems}
              onOpenNode={(nodeType, value, meta) => {
                const sectionId = nodeType === 'project'
                  ? 'projects'
                  : nodeType === 'location'
                    ? 'locations'
                    : nodeType === 'zone'
                      ? 'zones'
                      : nodeType === 'kit'
                        ? 'kits'
                        : nodeType === 'camera'
                          ? 'cameras'
                          : nodeType === 'sensor'
                            ? 'sensors'
                        : nodeType === 'threshold'
                          ? 'thresholds'
                          : nodeType;
                openArchitectureNode(sectionId, value, meta);
              }}
            />
          ) : (
            <>
          {section.id === 'client' && (
            <ConfigurationSectionPage
              section={section}
              layout="grid"
              columnPreferenceKey={columnPreferenceKeyFor('clients:v2')}
              items={scopedDataset.clients}
              columns={[
                { key: 'name', label: tr('client') },
                { key: 'slug', label: tr('slugUrl'), render: (item) => item.slug ? `/${item.slug}` : tr('noSlug') },
                { key: 'location', label: tr('location'), render: (item) => {
                  const clientLocation = getPrimaryLocationForClient(item.client_id);
                  return clientLocation ? formatLocationLabel(clientLocation) : tr('toConfirm');
                } },
                { key: 'species', label: tr('mainSpecies'), render: (item) => {
                  const clientProject = getPrimaryProjectForClient(item.client_id);
                  return clientProject?.species_focus || tr('toConfirm');
                } },
                { key: 'status', label: tr('status'), render: (item) => item.is_active ? 'active' : 'inactive' }
              ]}
              fields={clientFields}
              searchKeys={['name', 'slug', 'legal_name', 'timezone']}
              createLabel={tr('createClient')}
              onSave={handleSave('clients', 'client_id')}
              onDelete={handleDelete('clients', 'client_id')}
              destructiveDelete={{
                getName: (item) => item.name,
                title: 'Apagar cliente',
                description: (item) => `Vais apagar o cliente "${item.name}" e todos os dados associados: projetos, localizações, áreas monitorizadas, unidades edge, dispositivos, sensores, alertas, casos, capturas, métricas e utilizadores desse cliente.`,
                confirmationLabel: 'Escreve o nome exato do cliente para apagar',
                confirmButtonLabel: 'Apagar cliente e relacionados'
              }}
              canCreate={!isClientMode}
              canDelete={!isClientMode}
              searchPlaceholder={isClientMode ? tr('clientDataSearch') : undefined}
              emptyTitle={isClientMode ? tr('noClientData') : undefined}
              emptyDescription={isClientMode ? tr('noClientConfiguration') : undefined}
              createDraft={() => ({
                client_id: createId(),
                name: '',
                slug: '',
                is_active: true
              })}
            />
          )}

          {section.id === 'projects' && (
            <ConfigurationSectionPage
              section={section}
              layout="grid"
              columnPreferenceKey={columnPreferenceKeyFor('projects:v2')}
              items={scopedDataset.projects}
              columns={[
                { key: 'name', label: tr('project') },
                { key: 'slug', label: tr('slugUrl'), render: (item) => {
                  const clientItem = scopedDataset.clients.find((entry) => entry.client_id === item.client_id);
                  return clientItem?.slug && item.slug ? `/${clientItem.slug}/${item.slug}` : item.slug || tr('noSlug');
                } },
                { key: 'species_focus', label: 'Categoria principal', render: (item) => item.species_focus || getOperationalContextProfile(item.context_type).categoryPhaseFallback },
                { key: 'context_type', label: 'Contexto', render: (item) => getOperationalContextProfile(item.context_type).label },
                { key: 'default_phase', label: 'Fase', render: (item) => item.default_phase || 'Por definir' },
                { key: 'start_date', label: tr('start') },
                { key: 'status', label: tr('status'), render: (item) => item.is_active ? 'active' : 'inactive' }
              ]}
              fields={projectFields}
              searchKeys={['name', 'slug', 'context_type', 'species_focus', 'default_phase']}
              createLabel={tr('createProject')}
              metadata={tr('clientProject')}
              onSave={handleSave('projects', 'project_id')}
              onDelete={handleDelete('projects', 'project_id')}
              destructiveDelete={{
                getName: (item) => item.name,
                title: 'Apagar projeto',
                description: (item) => `Vais apagar o projeto "${item.name}" e todos os registos relacionados nas restantes secções: localizações, áreas monitorizadas, unidades edge, câmaras, sensores, sondas, regras, acessos específicos e histórico operacional associado.`,
                confirmationLabel: 'Escreve o nome exato do projeto para apagar',
                confirmButtonLabel: 'Apagar projeto e relacionados'
              }}
              createDraft={() => ({
                project_id: createId(),
                client_id: client?.client_id ?? '',
                name: '',
                slug: '',
                context_type: 'aquaculture',
                species_focus: '',
                default_phase: '',
                is_active: true
              })}
            />
          )}

          {section.id === 'locations' && (
            <ConfigurationSectionPage
              section={section}
              columnPreferenceKey={columnPreferenceKeyFor('locations')}
              items={scopedDataset.locations}
              columns={[
                { key: 'name', label: tr('installation') },
                { key: 'client_id', label: tr('client'), render: (item) => scopedDataset.clients.find((entry) => entry.client_id === item.client_id)?.name ?? tr('toConfirm') },
                { key: 'project_id', label: tr('project'), render: (item) => scopedDataset.projects.find((entry) => entry.project_id === item.project_id)?.name ?? tr('toConfirm') },
                { key: 'city', label: tr('city') },
                { key: 'country', label: tr('country') },
                { key: 'coordinates', label: tr('coordinates'), render: (item) => item.latitude != null && item.longitude != null ? `${Number(item.latitude).toFixed(5)}, ${Number(item.longitude).toFixed(5)}` : tr('toConfirm') },
                { key: 'status', label: tr('status'), render: (item) => item.is_active ? 'active' : 'inactive' }
              ]}
              fields={locationFields}
              searchKeys={['name', 'type', 'address', 'city', 'country', 'postal_code']}
              createLabel={tr('createLocation')}
              metadata={tr('projectSites')}
              onSave={handleSave('locations', 'location_id')}
              onDelete={handleDelete('locations', 'location_id')}
              createDraft={() => ({
                location_id: createId(),
                client_id: client?.client_id ?? '',
                project_id: project?.project_id ?? '',
                name: '',
                type: getDefaultOperationalLocationType(project?.context_type),
                address: '',
                city: '',
                postal_code: '',
                country: '',
                latitude: null,
                longitude: null,
                is_active: true
              })}
              mapConfig={{
                idKey: 'location_id',
                latitudeKey: 'latitude',
                longitudeKey: 'longitude',
                title: (item) => item.name,
                subtitle: (item) => [item.address, item.city, item.country].filter(Boolean).join(' / ') || tr('toConfirm')
              }}
            />
          )}

          {section.id === 'zones' && (
            <ConfigurationSectionPage
              section={section}
              layout="grid"
              columnPreferenceKey={columnPreferenceKeyFor('zones')}
              items={scopedDataset.zones}
              columns={[
                { key: 'name', label: tr('tank') },
                { key: 'project_id', label: tr('project'), render: (item) => scopedDataset.projects.find((projectItem) => projectItem.project_id === item.project_id)?.name ?? tr('toConfirm') },
                { key: 'location_id', label: tr('location'), render: (item) => scopedDataset.locations.find((locationItem) => locationItem.location_id === item.location_id)?.name ?? tr('toConfirm') },
                { key: 'status', label: tr('status'), render: (item) => item.is_active ? 'active' : 'inactive' },
                { key: 'phase', label: tr('biologicalPhase') },
                { key: 'species', label: tr('species') },
                { key: 'coordinates', label: tr('coordinates'), render: (item) => item.latitude != null && item.longitude != null ? `${Number(item.latitude).toFixed(5)}, ${Number(item.longitude).toFixed(5)}` : tr('toConfirm') }
              ]}
              fields={zoneFields}
              searchKeys={['name', 'code', 'type', 'species', 'phase', 'project_id', 'location_id']}
              createLabel={tr('createTank')}
              metadata={tr('hatchery')}
              renderCardFooter={(item) => {
                const zoneKits = scopedDataset.kits.filter((kitItem) => kitItem.zone_id === item.zone_id);
                const zoneCameras = scopedDataset.cameras.filter((cameraItem) => cameraItem.zone_id === item.zone_id || zoneKits.some((kitItem) => kitItem.kit_id === cameraItem.kit_id));
                const zoneSensors = scopedDataset.sensors.filter((sensorItem) => sensorItem.zone_id === item.zone_id || zoneKits.some((kitItem) => kitItem.kit_id === sensorItem.kit_id));
                if (!zoneKits.length && !zoneCameras.length && !zoneSensors.length) return null;

                return (
                  <div className="mt-4 border-t border-border/55 pt-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Operação ligada</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {zoneKits.map((kitItem) => (
                        <span key={kitItem.kit_id} className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-cyan-500/25 bg-cyan-500/10 px-2.5 py-1.5 text-xs font-bold text-cyan-700 dark:text-cyan-300">
                          <CpuChipIcon className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{kitItem.name || 'Unidade'}</span>
                        </span>
                      ))}
                      {zoneCameras.map((cameraItem) => (
                        <span key={cameraItem.camera_id} className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-sky-500/25 bg-sky-500/10 px-2.5 py-1.5 text-xs font-bold text-sky-700 dark:text-sky-300">
                          <CameraIcon className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{cameraItem.name || cameraItem.camera_code || 'Câmara'}</span>
                        </span>
                      ))}
                      {zoneSensors.slice(0, 6).map((sensorItem) => {
                        const normalizedType = String(sensorItem.sensor_type ?? '').toLowerCase();
                        const SensorIcon = sensorIconMap[normalizedType] ?? Cog6ToothIcon;
                        const label = sensorLabelMap[normalizedType] ?? sensorItem.name ?? 'Sensor';
                        return (
                          <span key={sensorItem.sensor_id} className={`inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-bold ${sensorToneClass(normalizedType)}`}>
                            <SensorIcon className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{label}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              }}
              onSave={(item, mode) => handleSave('zones', 'zone_id')(normalizeZoneScope(item) as AqlConfigurationDataset['zones'][number], mode)}
              onDelete={handleDelete('zones', 'zone_id')}
              createDraft={() => ({
                zone_id: createId(),
                location_id: location?.location_id ?? '',
                client_id: client?.client_id ?? '',
                project_id: project?.project_id ?? '',
                name: '',
                code: '',
                type: getOperationalContextProfile(project?.context_type).areaTypeOptions[0]?.value ?? 'operational_area',
                species: project?.species_focus ?? '',
                phase: project?.default_phase ?? '',
                latitude: null,
                longitude: null,
                is_active: true
              })}
              mapConfig={{
                idKey: 'zone_id',
                latitudeKey: 'latitude',
                longitudeKey: 'longitude',
                title: (item) => item.name,
                subtitle: (item) => [
                  scopedDataset.locations.find((locationItem) => locationItem.location_id === item.location_id)?.name,
                  item.species,
                  item.phase
                ].filter(Boolean).join(' · ') || tr('toConfirm')
              }}
            />
          )}

          {section.id === 'kits' && (
            <ConfigurationSectionPage
              section={section}
              layout="grid"
              columnPreferenceKey={columnPreferenceKeyFor('kits')}
              items={scopedDataset.kits}
              columns={[
                { key: 'name', label: 'Unidade' },
                { key: 'zone_id', label: 'Área associada', render: (item) => scopedDataset.zones.find((zoneItem) => zoneItem.zone_id === item.zone_id)?.name ?? tr('toConfirm') },
                { key: 'status', label: tr('status') },
                { key: 'heartbeat', label: tr('lastCommunication'), render: (item) => item.heartbeat ?? tr('noFeed') },
                { key: 'frame_interval_ms', label: 'Intervalo live', render: (item) => item.frame_interval_ms ? `${item.frame_interval_ms}ms` : tr('toConfirm') }
              ]}
              fields={kitFields}
              searchKeys={['name', 'kit_type', 'status', 'heartbeat']}
              createLabel={tr('createKit')}
              metadata={tr('operationalDevice')}
              toolbarActions={
                <button
                  type="button"
                  onClick={() => exportEdgePayload({ type: 'kit', label: 'kits', client, project, dataset: scopedDataset, all: true })}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-muted/35 px-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-primary" />
                  Exportar todos
                </button>
              }
              itemActions={(item) => (
                <button
                  type="button"
                  onClick={() => exportEdgePayload({ type: 'kit', label: item.name || item.kit_id, client, project, dataset: scopedDataset, kit: item })}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-muted"
                  aria-label="Exportar configuração do kit"
                  title="Exportar configuração do kit"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-primary" />
                </button>
              )}
              renderCardFooter={(item) => {
                const kitSensors = scopedDataset.sensors.filter((sensorItem) => sensorItem.kit_id === item.kit_id);
                const kitCameras = scopedDataset.cameras.filter((cameraItem) => cameraItem.kit_id === item.kit_id);
                if (!kitSensors.length && !kitCameras.length) return null;

                return (
                  <div className="mt-4 border-t border-border/55 pt-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Associados</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {kitCameras.map((cameraItem) => (
                        <span
                          key={cameraItem.camera_id}
                          className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-sky-500/25 bg-sky-500/10 px-2.5 py-1.5 text-xs font-bold text-sky-700 dark:text-sky-300"
                          title={cameraItem.name || cameraItem.camera_code || cameraItem.camera_id}
                        >
                          <CameraIcon className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{cameraItem.name || cameraItem.camera_code || 'Câmara'}</span>
                        </span>
                      ))}
                      {kitSensors.map((sensorItem) => {
                        const normalizedType = String(sensorItem.sensor_type ?? '').toLowerCase();
                        const SensorIcon = sensorIconMap[normalizedType] ?? Cog6ToothIcon;
                        const label = sensorLabelMap[normalizedType] ?? sensorItem.name ?? 'Sensor';
                        const value = typeof sensorItem.last_value === 'number'
                          ? `${sensorItem.last_value}${sensorItem.unit ? ` ${sensorItem.unit}` : ''}`
                          : null;

                        return (
                          <span
                            key={sensorItem.sensor_id}
                            className={`inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-bold ${sensorToneClass(normalizedType)}`}
                            title={sensorItem.name || label}
                          >
                            <SensorIcon className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{label}</span>
                            {value ? <span className="text-foreground/70">{value}</span> : null}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              }}
              renderEditorExtra={(draft, setDraft) => scopedDataset.kits.some((item) => item.kit_id === String(draft.kit_id ?? '')) ? (
                <KitVisionRuntimeControls
                  kitId={String(draft.kit_id)}
                  onAssignmentChange={(assignment) => setDraft((current) => ({ ...current, ...assignment }))}
                />
              ) : null}
              onSave={(item, mode) => handleSave('kits', 'kit_id')(normalizeKitScope(item) as AqlConfigurationDataset['kits'][number], mode)}
              onDelete={handleDelete('kits', 'kit_id')}
              createDraft={() => ({
                kit_id: createId(),
                client_id: isClientMode ? client?.client_id ?? '' : '',
                location_id: isClientMode ? location?.location_id ?? '' : '',
                project_id: isClientMode ? project?.project_id ?? '' : '',
                zone_id: isClientMode ? zone?.zone_id ?? '' : '',
                name: '',
                kit_type: 'vision',
                controller_profile: 'atronia-atlas',
                connectivity_mode: 'ethernet',
                offline_buffer_hours: 72,
                bulk_batch_size: 500,
                local_alerting_enabled: true,
                configuration_revision: 1,
                status: 'active',
                description: '',
                capture_interval_seconds: 60,
                frame_interval_ms: 1000,
                storage_bucket: 'captures',
                jpg_quality: 70,
                live_frame_path_template: 'live/{camera_code}/feed.jpg',
                vision_lab_project_id: null,
                vision_model_policy: 'latest_approved',
                vision_model_version: null,
                video_acquisition_enabled: true,
                sensor_acquisition_enabled: true,
                is_active: true
              })}
            />
          )}

          {section.id === 'cameras' && (
            <ConfigurationSectionPage
              section={section}
              layout="grid"
              columnPreferenceKey={columnPreferenceKeyFor('cameras')}
              items={cameraItems}
              columns={[
                { key: 'name', label: 'Câmara' },
                { key: 'camera_code', label: 'Código QR', render: (item) => item.camera_code ?? '--' },
                { key: 'kit_id', label: 'Unidade', render: (item) => scopedDataset.kits.find((kit) => kit.kit_id === item.kit_id)?.name ?? 'Sem unidade' },
                { key: 'live_feed_path', label: 'Feed live', render: (item) => item.live_feed_path ?? '--' },
                { key: 'last_frame_status', label: 'Feed', render: (item) => item.last_frame_status ?? 'sem frame' },
                { key: 'training_burst', label: 'Treino', render: (item) => item.training_burst_available ? 'recolher dados' : 'escondido' },
                { key: 'frame_interval_ms', label: 'Intervalo live', render: (item) => item.frame_interval_ms ? `${item.frame_interval_ms}ms` : '--' },
                { key: 'status', label: 'Status', render: (item) => item.is_active ? 'active' : 'inactive' }
              ]}
              fields={cameraFields}
              searchKeys={['name', 'camera_code', 'source_type', 'source_uri', 'live_feed_path', 'training_burst_path', 'resolution', 'position_description']}
              createLabel="Criar câmara"
              metadata="QR -> Storage live -> Unidade opcional"
              toolbarActions={
                <button
                  type="button"
                  onClick={() => exportEdgePayload({ type: 'camera', label: 'camaras', client, project, dataset: scopedDataset, all: true })}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-muted/35 px-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-primary" />
                  Exportar todas
                </button>
              }
              itemActions={(item) => (
                <button
                  type="button"
                  onClick={() => exportEdgePayload({
                    type: 'camera',
                    label: item.name || item.camera_code || item.camera_id,
                    client,
                    project,
                    dataset: scopedDataset,
                    camera: item,
                    kit: item.kit_id ? scopedDataset.kits.find((kitItem) => kitItem.kit_id === item.kit_id) ?? null : null
                  })}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-muted"
                  aria-label="Exportar configuração da câmara"
                  title="Exportar configuração da câmara"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-primary" />
                </button>
              )}
              onSave={(item, mode) => handleSave('cameras', 'camera_id')(normalizeCameraForSave(item), mode)}
              onDelete={handleDelete('cameras', 'camera_id')}
              createDraft={() => {
                const cameraCode = createCameraCode();
                const liveFeedPath = liveFeedPathForCameraCode(cameraCode);
                const defaultClientId = isClientMode ? client?.client_id ?? '' : '';
                const defaultProjectId = isClientMode ? project?.project_id ?? '' : '';
                const defaultLocationId = isClientMode ? location?.location_id ?? '' : '';
                const defaultZoneId = isClientMode ? zone?.zone_id ?? '' : '';
                return {
                  camera_id: createId(),
                  client_id: defaultClientId,
                  project_id: defaultProjectId,
                  location_id: defaultLocationId,
                  kit_id: null,
                  zone_id: defaultZoneId,
                  name: '',
                  camera_code: cameraCode,
                  source_type: 'storage',
                  source_uri: liveFeedPath,
                  live_feed_path: liveFeedPath,
                  last_frame_at: null,
                  last_frame_status: null,
                  frame_interval_ms: 1000,
                  training_burst_available: false,
                  training_burst_enabled: false,
                  training_burst_target: 50,
                  training_burst_count: 0,
                  training_burst_path: trainingBurstPathForCameraCode(cameraCode),
                  training_burst_started_at: null,
                  training_burst_completed_at: null,
                  resolution: '1920x1080',
                  position_description: '',
                  is_primary: false,
                  is_active: true
                };
              }}
            />
          )}

          {section.id === 'ultrasound' && (
            <ConfigurationSectionPage
              section={section}
              layout="grid"
              columnPreferenceKey={columnPreferenceKeyFor('ultrasound-devices')}
              items={ultrasoundItems}
              columns={[
                { key: 'name', label: 'Equipamento' },
                { key: 'kit_id', label: 'Unidade', render: (item) => scopedDataset.kits.find((kit) => kit.kit_id === item.kit_id)?.name ?? 'Sem unidade' },
                { key: 'zone_id', label: 'Área associada', render: (item) => scopedDataset.zones.find((zoneItem) => zoneItem.zone_id === item.zone_id)?.name ?? tr('toConfirm') },
                { key: 'status', label: tr('status') },
                { key: 'is_active', label: 'Ativo', render: (item) => item.is_active ? 'active' : 'inactive' }
              ]}
              fields={ultrasoundFields}
              searchKeys={['name', 'status', 'description']}
              createLabel="Criar ultrassons"
              metadata="US -> Unidade -> Live preview"
              onSave={(item, mode) => handleSave('ultrasoundDevices', 'device_id')(normalizeUltrasoundForSave(item), mode)}
              onDelete={handleDelete('ultrasoundDevices', 'device_id')}
              createDraft={() => {
                const defaultClientId = isClientMode ? client?.client_id ?? '' : '';
                const defaultProjectId = isClientMode ? project?.project_id ?? '' : '';
                const defaultLocationId = isClientMode ? location?.location_id ?? '' : '';
                const defaultZoneId = isClientMode ? zone?.zone_id ?? '' : '';
                const defaultKit = isClientMode
                  ? getFirstKitForScope(defaultClientId, defaultProjectId, defaultLocationId, defaultZoneId)
                  : null;
                return {
                  device_id: createId(),
                  client_id: defaultClientId,
                  project_id: defaultProjectId,
                  location_id: defaultLocationId,
                  zone_id: defaultKit?.zone_id ?? defaultZoneId,
                  kit_id: defaultKit?.kit_id ?? '',
                  name: '',
                  device_type: 'ultrasound' as const,
                  status: 'active',
                  description: '',
                  is_active: true
                };
              }}
            />
          )}

          {section.id === 'sensors' && (
            <ConfigurationSectionPage
              section={section}
              layout="grid"
              columnPreferenceKey={columnPreferenceKeyFor('sensors')}
              items={sensorItems}
              columns={[
                { key: 'name', label: tr('sensor') },
                { key: 'sensor_type', label: tr('sensorType') },
                { key: 'min_threshold', label: tr('minimum'), render: (item) => item.min_threshold ?? tr('toConfirm') },
                { key: 'max_threshold', label: tr('maximum'), render: (item) => item.max_threshold ?? tr('toConfirm') },
                { key: 'last_value', label: tr('lastValue'), render: (item) => item.last_value ?? tr('noFeed') },
                { key: 'status', label: tr('status'), render: (item) => item.is_active ? 'active' : 'inactive' }
              ]}
              fields={sensorFields}
              searchKeys={['name', 'sensor_type', 'unit']}
              createLabel={tr('createSensor')}
              toolbarActions={
                <button
                  type="button"
                  onClick={() => exportEdgePayload({ type: 'sensor', label: 'sensores', client, project, dataset: scopedDataset, all: true })}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-muted/35 px-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-primary" />
                  Exportar todos
                </button>
              }
              itemActions={(item) => (
                <button
                  type="button"
                  onClick={() => exportEdgePayload({
                    type: 'sensor',
                    label: item.name || item.sensor_type || item.sensor_id,
                    client,
                    project,
                    dataset: scopedDataset,
                    sensor: item,
                    kit: item.kit_id ? scopedDataset.kits.find((kitItem) => kitItem.kit_id === item.kit_id) ?? null : null
                  })}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-muted"
                  aria-label="Exportar configuração do sensor"
                  title="Exportar configuração do sensor"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-primary" />
                </button>
              )}
              onSave={(item, mode) => handleSave('sensors', 'sensor_id')(normalizeSensorForSave(item), mode)}
              onDelete={handleDelete('sensors', 'sensor_id')}
              createDraft={() => {
                const defaultClientId = isClientMode ? client?.client_id ?? '' : '';
                const defaultProjectId = isClientMode ? project?.project_id ?? '' : '';
                const defaultLocationId = isClientMode ? location?.location_id ?? '' : '';
                const defaultZoneId = isClientMode ? zone?.zone_id ?? '' : '';
                const defaultKit = isClientMode
                  ? getFirstKitForScope(defaultClientId, defaultProjectId, defaultLocationId, defaultZoneId)
                  : null;
                return {
                  sensor_id: createId(),
                  client_id: defaultClientId,
                  project_id: defaultProjectId,
                  location_id: defaultLocationId,
                  kit_id: defaultKit?.kit_id ?? '',
                  zone_id: defaultKit?.zone_id ?? defaultZoneId,
                  sensor_type: '',
                  name: '',
                  unit: '',
                  is_active: true
                };
              }}
            />
          )}

          {section.id === 'vision-copilot' && (
            <ConfigurationSectionPage
              section={section}
              layout="grid"
              columnPreferenceKey={columnPreferenceKeyFor('vision-copilot')}
              items={copilotItems}
              columns={[
                { key: 'project_id', label: tr('project'), render: (item) => scopedDataset.projects.find((entry) => entry.project_id === item.project_id)?.name ?? tr('toConfirm') },
                { key: 'sentinel_enabled', label: 'Sentinela', render: (item) => item.sentinel_enabled ? 'active' : 'inactive' },
                { key: 'analyze_on_alert', label: 'Prioridade em alerta', render: (item) => item.analyze_on_alert ? 'active' : 'inactive' },
                { key: 'analysis_interval_minutes', label: 'Cadência', render: (item) => `${item.analysis_interval_minutes} min` },
                { key: 'monthly_tokens_used', label: 'Tokens usados' },
                { key: 'monthly_token_budget', label: 'Orçamento tokens' },
                { key: 'cost_per_1k_tokens_eur', label: 'EUR/1k tokens', render: (item) => Number(item.cost_per_1k_tokens_eur || 0).toFixed(4) },
                { key: 'estimated_monthly_cost_eur', label: 'Custo EUR', render: (item) => Number(item.estimated_monthly_cost_eur || 0).toFixed(2) }
              ]}
              fields={copilotFields}
              searchKeys={['model', 'system_prompt']}
              createLabel="Configurar Copilot"
              metadata="Gemini multimodal"
              notice="O Copilot só gasta tokens automaticamente quando o Modo Sentinela e a análise em alerta estão ativos. O orçamento mensal é usado como guarda operacional."
              canCreate={!selectedCopilotSetting}
              canDelete={!isClientMode}
              onSave={saveCopilotSetting}
              onDelete={handleDelete('projectSettings', 'setting_id')}
              createDraft={() => ({
                setting_id: createId(),
                client_id: client?.client_id ?? '',
                project_id: project?.project_id ?? '',
                key: VISION_COPILOT_SETTING_KEY,
                sentinel_enabled: false,
                analyze_on_alert: true,
                add_alarm_context: true,
                analysis_interval_minutes: 60,
                model: '',
                max_output_tokens: 700,
                monthly_token_budget: 250000,
                monthly_tokens_used: 0,
                cost_per_1k_tokens_eur: 0,
                estimated_monthly_cost_eur: 0,
                last_sentinel_analysis_at: '',
                system_prompt: DEFAULT_COPILOT_PROMPT
              })}
              emptyTitle="Copilot ainda não configurado"
              emptyDescription="Cria uma configuração por projeto para ativar o Modo Sentinela, controlar o prompt e limitar gastos."
            />
          )}

          {section.id === 'species-phases' && (
            <div className="space-y-4">
              <ConfigurationSectionPage
                section={section}
                columnPreferenceKey={columnPreferenceKeyFor('species')}
                items={scopedDataset.species}
                columns={[
                  { key: 'common_name', label: 'Common name' },
                  { key: 'scientific_name', label: 'Scientific name' },
                  { key: 'code', label: 'Code' },
                  { key: 'status', label: 'Status', render: (item) => item.is_active ? 'active' : 'inactive' }
                ]}
                fields={speciesFields}
                searchKeys={['common_name', 'scientific_name', 'code']}
                createLabel="Create species"
                metadata="Biological setup"
                onSave={handleSave('species', 'species_id')}
                onDelete={handleDelete('species', 'species_id')}
                createDraft={() => ({
                  species_id: createId(),
                  client_id: client?.client_id ?? '',
                  project_id: project?.project_id ?? '',
                  common_name: '',
                  scientific_name: '',
                  code: slugify('species'),
                  is_active: true
                })}
              />

              <ConfigurationSectionPage
                section={{ ...section, title: 'Phases', subtitle: 'Lifecycle stages used by operations and biological reporting.' }}
                columnPreferenceKey={columnPreferenceKeyFor('phases')}
                items={scopedDataset.phases}
                columns={[
                  { key: 'name', label: 'Phase' },
                  { key: 'code', label: 'Code' },
                  { key: 'sort_order', label: 'Sort order' },
                  { key: 'status', label: 'Status', render: (item) => item.is_active ? 'active' : 'inactive' }
                ]}
                fields={phaseFields}
                searchKeys={['name', 'code']}
                createLabel="Create phase"
                metadata="Lifecycle phases"
                onSave={handleSave('phases', 'phase_id')}
                onDelete={handleDelete('phases', 'phase_id')}
                createDraft={() => ({
                  phase_id: createId(),
                  client_id: client?.client_id ?? '',
                  project_id: project?.project_id ?? '',
                  name: '',
                  code: '',
                  sort_order: scopedDataset.phases.length * 10 + 10,
                  is_active: true
                })}
              />
            </div>
          )}

          {section.id === 'thresholds' && (
            <div>
              <AlertRuleManager
                client={client}
                project={project}
                zones={scopedDataset.zones}
              />
            </div>
          )}

          {section.id === 'users' && (
            <ConfigurationSectionPage
              section={section}
              layout="grid"
              columnPreferenceKey={columnPreferenceKeyFor('users')}
              items={userItems}
              columns={[
                {
                  key: 'identity',
                  label: 'Utilizador',
                  render: (item: AqlUserAccess) => (
                    <div className="min-w-[230px]">
                      <p className="font-medium text-foreground">{item.name || item.username || item.email}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{item.email}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{item.job_title || `@${item.username || '--'}`}</p>
                    </div>
                  )
                },
                {
                  key: 'scope',
                  label: 'Clientes / projetos',
                  render: (item: AqlUserAccess) => {
                    const userClient = scopedDataset.clients.find((client) => client.client_id === item.client_id);
                    const userClientIds = Array.from(new Set([
                      ...((item.client_ids ?? []).map(String).filter(Boolean)),
                      ...(item.client_id ? [item.client_id] : [])
                    ]));
                    const clientNames = userClientIds
                      .map((clientId) => scopedDataset.clients.find((client) => client.client_id === clientId)?.name ?? clientId)
                      .filter(Boolean);
                    const clientLabel = clientNames.length
                      ? clientNames.length > 2
                        ? `${clientNames.slice(0, 2).join(', ')} +${clientNames.length - 2}`
                        : clientNames.join(', ')
                      : userClient?.name ?? item.client_id ?? '--';
                    const userProjectIds = Array.from(new Set([
                      ...((item.project_ids ?? []).map(String).filter(Boolean)),
                      ...(item.project_id ? [item.project_id] : [])
                    ]));
                    const projectNames = userProjectIds
                      .map((projectId) => scopedDataset.projects.find((project) => project.project_id === projectId)?.name ?? projectId)
                      .filter(Boolean);
                    const projectLabel = projectNames.length
                      ? projectNames.length > 2
                        ? `${projectNames.slice(0, 2).join(', ')} +${projectNames.length - 2}`
                        : projectNames.join(', ')
                      : userClientIds.length > 1
                        ? 'Todos os projetos dos clientes selecionados'
                        : 'Todos os projetos do cliente';
                    return (
                      <div className="min-w-[170px]">
                        <p className="font-medium text-foreground">{clientLabel}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{projectLabel}</p>
                      </div>
                    );
                  }
                },
                { key: 'role', label: 'Role', render: (item: AqlUserAccess) => item.role },
                {
                  key: 'auth_user_id',
                  label: 'Auth',
                  render: (item: AqlUserAccess) => item.auth_user_id ? 'Ligado' : 'Convite pendente'
                },
                { key: 'job_title', label: 'Função', render: (item: AqlUserAccess) => item.job_title ?? '--' },
                { key: 'mobile_phone', label: 'SMS', render: (item: AqlUserAccess) => item.mobile_phone ?? '--' },
                { key: 'alert_notifications_enabled', label: 'Alertas', render: (item: AqlUserAccess) => item.alert_notifications_enabled ? 'Sim' : 'Não' }
              ]}
              fields={userFields}
              searchKeys={['name', 'job_title', 'username', 'email', 'mobile_phone', 'role', 'client_id', 'client_ids', 'project_id', 'project_ids']}
              createLabel="Criar utilizador"
              metadata={isClientMode ? 'Perfil operacional' : 'Supabase Auth profile'}
              onSave={handleUserSave}
              onDelete={handleDelete('users', 'user_id')}
              canCreate={!isClientMode && Boolean(portalAccess?.accessToken)}
              canEdit={Boolean(portalAccess?.accessToken)}
              canDelete={false}
              createDraft={() => ({
                user_id: createId(),
                auth_user_id: null,
                client_id: client?.client_id ?? '',
                client_ids: client?.client_id ? [client.client_id] : [],
                project_id: isClientMode && resolvedProjectSlug ? project?.project_id ?? null : null,
                project_ids: isClientMode && resolvedProjectSlug && project?.project_id ? [project.project_id] : [],
                project_scope: isClientMode && resolvedProjectSlug ? 'specific' : 'all',
                name: '',
                job_title: '',
                username: '',
                email: '',
                mobile_phone: '',
                alert_notifications_enabled: true,
                role: 'viewer',
                is_active: true
              })}
            />
          )}

          {section.id === 'personalization' && (
            <PersonalizationSettings />
          )}
            </>
          )}
          {architectureEditor ? (
            <ConfigurationDetailSheet
              open={Boolean(architectureSelection)}
              onOpenChange={(next) => {
                if (!next) {
                  const draftNodeId = architectureSelection?.draftNodeId;
                  if (draftNodeId) {
                    setDiscardDraftNodeId(draftNodeId);
                    savedDraftNodeIdsRef.current.delete(draftNodeId);
                  }
                  setArchitectureSelection(null);
                }
              }}
              mode={architectureSelection?.mode ?? 'edit'}
              title={architectureEditor.section.title}
              description={architectureEditor.section.subtitle}
              fields={architectureEditor.fields}
              value={architectureEditor.value}
              renderAfterFields={(draft, setDraft) => architectureSelection?.sectionId === 'kits' && scopedDataset.kits.some((item) => item.kit_id === String(draft.kit_id ?? '')) ? (
                <KitVisionRuntimeControls
                  kitId={String(draft.kit_id)}
                  onAssignmentChange={(assignment) => setDraft((current) => ({ ...current, ...assignment }))}
                />
              ) : null}
              onSave={async (next) => {
                const mode = architectureSelection?.mode ?? 'edit';
                const draftNodeId = architectureSelection?.draftNodeId;
                await architectureEditor.onSave(next, mode);
                if (draftNodeId) {
                  savedDraftNodeIdsRef.current.add(draftNodeId);
                  setDiscardDraftNodeId(draftNodeId);
                }
              }}
              onDelete={async (value) => {
                await architectureEditor.onDelete(value);
                setArchitectureSelection(null);
              }}
              deleteLabel="Apagar nó"
            />
          ) : null}
        </div>
      </section>
    </div>
  );
}
